/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
import * as util from "../../../../common/util";
import { promisify, isWindows } from "../../../../common/util";
import { System } from "../../../../common/system";
import { Logger } from "../../../../clients/logger";
import {
	ListenerResponse,
	RouterMessage,
	RouterClient,
	ResponderMessage,
	IRouterClient,
} from "../../../../clients/routerClient";
import { ConfigClient } from "../../../../clients/configClient";
import { ConfigUtilInstance as ConfigUtil } from "../../../../common/configUtil";
import { WorkspaceClient } from "../../../../clients/workspaceClient";
import { promoteTab, WindowClient } from "../../../../clients/windowClient";
import { LauncherGroup as LauncherWindowGroup } from "./launcherGroup";
import { FinsembleWindowInternal } from "../WindowAbstractions/FinsembleWindowInternal";
import { BaseWindow } from "../WindowAbstractions/BaseWindow";
import { CreateSplinterAndInject } from "./createSplinterAndInject";
import LauncherDefaults, { UNKNOWN_DEFAULT_CONFIG } from "./LauncherDefaults";
import StorageClient from "../../../../clients/storageClient";
import { StandardCallback, StandardError, StandardErrorCallback, Manifest, FinsembleConfig } from "../../../../types";
import {
	WindowIdentifier,
	WindowDescriptor,
	SpawnParams,
	WindowTypes,
	PositionTypes,
	ComponentConfig,
	ShowWindowParams,
	WindowInterop,
	TabbedGlobalSpawnParams,
} from "../types";
import { FSBLWindow, WindowServiceStoreInstance } from "../localTypes";
import {
	adjustBoundsToBeOnMonitor,
	getMonitorFromScaledXY,
	getMonitorFromWindow,
	getAllMonitors,
	onMonitorsChanged,
	Monitors,
	MonitorCommand,
	Bounds,
} from "../../../../common/Monitors";
import { MonitorInfoDetail, FEAWindow } from "../../../../FEA";
import { addWindowReferenceToActiveWorkspace } from "../../workspace/workspaceAdminAPI";
import { HEARTBEAT_TIMEOUT_CHANNEL, LAUNCHER_SERVICE, WINDOWSTATE } from "../../../../common/constants";
import SpawnUtils from "../Common/spawnUtils";
import {
	processLaunchGroupComponentConfiguration,
	generateDynamicAuthenticationToken,
	updateCustomDataWithAuthToken,
} from "./componentUtils";
import DockingMain from "../Docking/dockingMain";
import FinsembleEvent from "../../../../common/events/FinsembleEvent";
import { getIcons } from "./Icons";
import { nameMap as legacyUINameMap } from "../../../../deprecated/legacyUIComponentNames";
import { InteropAdmin } from "../../../../clients/Interop/InteropAdmin";
import { actions } from "../modules/actions";
import uuidv4 from "uuid-random";
import _ from "lodash";
import get from "lodash/get";
import set from "lodash/set";
import merge from "deepmerge";
import { each, eachOf as asyncEachOf, parallel as asyncParallel, series as asyncSeries } from "async";
import { SpawnTabCollectionResponse } from "../../../../clients/appsClient";
import { FinsembleWindow } from "../../../../common/FinsembleWindow";
import jsonMergePatch from "json-merge-patch";
import { sendCommandToWindowService } from "../modules/main";

type ComponentMap = Record<string, ComponentConfig>;

interface AdjustedSpawnParams extends SpawnParams {
	monitors: MonitorInfoDetail[];
	previousMonitor: MonitorInfoDetail;
	previousWindow: FSBLWindow;
	previousWindowBounds: Bounds;
	width: number | string;
	height: number | string;
	forceOntoMonitor: boolean | string;
	isInExistingGroup: boolean;
}

interface ILauncherGroup {
	getWindows(): { [name: string]: FSBLWindow };
	getWindow(name: string): FSBLWindow;
	findAllByComponentType(componentType: string): FSBLWindow[];
	getWindowNames(): string[];
	addWindow(window: FSBLWindow): void;
	removeWindows(windowNames: string[], cb: () => void);
	windows: FSBLWindow[];
}

// @ts-ignore LauncherWindowGroup extends WindowGroup, which has the remaining ILauncherGroup methods
export const activeWindows: ILauncherGroup = new LauncherWindowGroup({
	name: "LauncherService.allWindows",
});

type GetWhichMonitorParams = {
	commandMonitor: MonitorCommand;
	monitors: MonitorInfoDetail[];
	launchingMonitorPosition: number;
	windowIdentifier: WindowIdentifier;
	activeWindows: ILauncherGroup;
};

/**
 * Gets the monitor given a "command" where command can be "primary", "previous", "next" or a number representing a specific monitor.
 *
 * Used specifically by launcher. Don't use this in client code.
 */
const getWhichMonitor = (params: GetWhichMonitorParams) =>
	new Promise<MonitorInfoDetail>((resolve) => {
		let monitor;
		let { commandMonitor, monitors, launchingMonitorPosition } = params;
		if (commandMonitor === "mine") {
			const w = params.activeWindows.getWindow(params.windowIdentifier.windowName);
			if (w) {
				w._getBounds({}, async (err, bounds) => {
					if (!err) {
						const calculatedMonitor = await getMonitorFromScaledXY(bounds.left, bounds.top);
						resolve(calculatedMonitor);
					} else {
						monitor = monitors[0];
						resolve(monitor);
					}
				});
			}
			// If there is not a window use the first  monitor.
			// It happens when call a `showWindow()` from the custom service, which doesn't have a UI window
			else {
				monitor = monitors[0];
				resolve(monitor);
			}

			// resolve when _getBounds() finishes above
			return;
		} else if (commandMonitor === "primary") {
			monitor = monitors.find((m) => m.whichMonitor === "primary");
		} else if (commandMonitor === "next") {
			let position = launchingMonitorPosition + 1;
			if (position >= monitors.length) {
				position = 0;
			}
			monitor = monitors[position];
		} else if (commandMonitor === "previous") {
			let position = launchingMonitorPosition - 1;
			if (position < 0) {
				position = monitors.length - 1;
			}
			monitor = monitors[position];
		} else if (typeof commandMonitor === "number") {
			if (commandMonitor >= monitors.length) {
				commandMonitor = monitors.length - 1;
			}
			monitor = monitors.filter((knownMonitor) => knownMonitor.position === commandMonitor)[0];
		} else if (launchingMonitorPosition) {
			monitor = monitors[launchingMonitorPosition];
		}

		if (!monitor) {
			// primary if no monitor found
			monitor = monitors[0];
		}

		resolve(monitor);
	});

/**
 * Daniel H. 1/2/19
 * Our use of `this` here causes every instance of Launcher
 * overwrite all the others.
 */
declare let self: Launcher;

const NAME_STORAGE_KEY = "finsemble.NameCountData";

export let Components: any = {};

const _isOfPositionTypes = (positionType: string): positionType is PositionTypes =>
	["available", "monitor", "relative", "virtual", "absolute"].includes(positionType);

const _isLaunchGroupComponent = (componentConfiguration): boolean => {
	// We will need to remove this check when we implement true composite components
	if (componentConfiguration?.window?.windowType === "composite") {
		Logger.system.deprecated('windowType: "composite" is deprecated - please use windowType: "launchGroup"');
		componentConfiguration.window.windowType = "launchGroup";
	}

	return componentConfiguration?.window?.windowType === "launchGroup";
};

/**
 * Returns the extension for a url, if it has an extension, otherwise returns null
 */
const getUrlExtension = (url: string): string | null => {
	try {
		const pathName = new URL(url).pathname;
		const fileName = pathName.replace(/^.*[\\\/]/, "");
		const maybeExtension = fileName.split(".").pop() ?? null;
		return maybeExtension === fileName ? null : maybeExtension;
	} catch (e) {
		return null;
	}
};

/**
 * Returns a window title bar url given a url entry that might be either a directory
 * or an actual file. The requested extension, either js or html, is added so that this
 * can be used either as a browserview or injected titlebar. If the provided url is a
 * directory then index.js/index.html is assumed.
 *
 * @param entry Url of where to find the window title bar
 * @param extension Either "js" or "html"
 * @returns A definitive url
 */
export const calculateWindowTitleBarUrl = (url: string, extension: "js" | "html"): string => {
	// Get the file extension if one exists
	const existingExtension = getUrlExtension(url);
	// If extension exists, replace with .js or .html
	if (existingExtension) return `${url.substr(0, url.lastIndexOf("."))}.${extension}`;
	// Ensure trailing slash
	const entryWithSlash = url.replace(/\/?$/, "/");
	// Append index.js or index.html
	return `${entryWithSlash}index.${extension}`;
};

const _determineTitleBarType = (
	finsembleConfig: FinsembleConfig,
	componentConfig: ComponentConfig | null,
	componentName: string
): "browserview" | "injected" => {
	const localTitlebarType = componentConfig?.foreign?.components?.["Window Manager"]?.titlebarType;
	const globalTitlebarType = finsembleConfig["Window Manager"].titlebarType;
	const disableBrowserViewType =
		finsembleConfig["Window Manager"]?.disableBrowserView ||
		componentConfig?.foreign?.components?.["Window Manager"]?.disableBrowserView
			? "injected"
			: null;

	//use localTitlebarType if set, then disableBrowserView (for backwards compatibility), then globalTitlebarType (which should always be set)
	const titlebarType = localTitlebarType ?? disableBrowserViewType ?? globalTitlebarType ?? "browserview";
	Logger.debug(
		`_determineTitleBarType for ${componentName}: localTitlebarType: ${localTitlebarType}, globalTitlebarType: ${globalTitlebarType}, disableBrowserViewType: ${disableBrowserViewType}, titlebarType chosen: ${titlebarType}`
	);
	return titlebarType;
};

/**
 * Get the URL registered to a given preload, based on its alias
 */
const getPreloadUrlFromAlias = (preloadAlias: string, finsembleConfig: FinsembleConfig): string => {
	let preloadUrl = "";
	if (finsembleConfig.preloads) {
		for (const k in finsembleConfig.preloads) {
			const { alias, url } = finsembleConfig.preloads[k];
			if (alias === preloadAlias) {
				preloadUrl = url;
				break;
			}
		}
	}
	if (finsembleConfig.extensions?.preloads) {
		for (const k in finsembleConfig.extensions.preloads) {
			const { url } = finsembleConfig.extensions.preloads[k];
			if (k === preloadAlias) {
				preloadUrl = url;
				break;
			}
		}
	}
	return preloadUrl;
};

/**
 * Returns a url for a path that may either be an actual url or which may refer
 * to a preload alias. If The path is not a valid url, then the finsembleConfig.preload
 * map is checked to see whether there is a matching alias, and if so the url for that
 * alias is returned.
 *
 * @param path The file path
 */
const maybeResolvePreloadAllias = (path: string, finsembleConfig: FinsembleConfig): string => {
	let url;
	try {
		url = new URL(path).href;
	} catch (e) {
		url = getPreloadUrlFromAlias(path, finsembleConfig);
		if (!url && path) {
			Logger.system.error(`Invalid preload '${path}'. Invalid url and unable to match preload alias`);
		}
	}
	return url;
};

/**
 * Returns the window title bar url so that it can be passed to FEA. This function follows these rules of precedence:
 * 1) A windowTitleBarUrl that is passed to spawn()
 * 2) A specifically configured window title bar for this component (config.window.options.windowTitleBarUrl)
 * 3) Legacy specifically configured window title bar (config.components["Window Manager"].windowTitleBarUrl)
 * 4) Current WindowTitleBar component config (capitalized)
 * 5) Legacy windowTitleBar component config (lowercase)
 * 6) Default window title bar location as specified by manifest or FEA
 *
 * It then completes the url by looking it up against preload aliases, and then appending index.js if necessary,
 * and finally ensuring that its extension is .js (FEA will convert to .html where necessary).
 *
 * @param param.windowTitleBarUrl If provided as an option to spawn
 * @param param.componentConfig The configuration for the component that is being opened
 * @param param.finsembleConfig The "finsemble" portion of config. This should contain the default windowTitleBarUrl as set by FEA.
 * @returns The url for the window title bar
 */
export const deriveWindowTitleBarUrl = ({
	windowTitleBarUrl,
	componentConfig,
	finsembleConfig,
}: {
	windowTitleBarUrl?: string;
	componentConfig: ComponentConfig;
	finsembleConfig: FinsembleConfig;
}): string => {
	if (componentConfig.components?.["Window Manager"]?.windowTitleBarUrl)
		Logger.system.deprecated(
			'["Window Manager"].windowTitleBarUrl is deprecated, please use window.options.windowTitleBarUrl'
		);

	const url =
		windowTitleBarUrl ??
		componentConfig.window?.options?.windowTitleBarUrl ??
		componentConfig.components?.["Window Manager"]?.windowTitleBarUrl ?? // @deprecated - will be removed in the next major version (7)
		Components["WindowTitleBar"]?.window?.url ??
		Components["windowTitleBar"]?.window?.url ??
		finsembleConfig.windowTitleBarUrl;

	// If any of the above result in a preload alias rather than a url then resolve that alias
	// Then resolve resolve the url according to the rules i calculateWindowTitleBarUrl
	return calculateWindowTitleBarUrl(maybeResolvePreloadAllias(url, finsembleConfig), "js");
};

export interface LauncherResponseMessage extends ResponderMessage {
	functionName: string;
}

type PendingWindow = {
	componentType: string;
	addToWorkspace: boolean;
	windowType: string;
};

const NATIVE_WINDOW_TYPES = [
	"assimilation",
	"assimilated",
	"native",
	"NativeWindow",
	"FinsembleNativeWindow",
	"Citrix",
	"external",
];

/**
 * The Launcher Service receives calls from the launcherClient, and spawns windows.

 */
export class Launcher {
	/** @alias Launcher# */
	createSplinterAndInject: CreateSplinterAndInject;

	// Window groups that are created via the launcher client.
	// deprecated, these groups have nothing to do with docking groups. All code related to launcher groups
	// should be deleted when the currently deprecated client APIs are removed
	windowGroups: object = {};

	// Map of info about the last window that was opened.
	lastOpenedMap: object = {};

	lastAdjustedMap: object = {};

	// Whether to persist URL changes on the component.
	persistURL: boolean = false;

	// Whether to persist the component's path (the path to the component in the OS). Default to true.
	persistPath: boolean = false;

	// Whether to persist the component's arguments (the arguments passed to the component). Default to true.
	persistArguments: boolean = true;

	// When we're shutting down, we ignore spawn requests. This gets set to true.
	shuttingDown: boolean = false;

	// Local copy of monitors, this will prevent us from having to fetch them every time
	monitors: any = null;

	interopAdmin: InteropAdmin;

	/**
	 * Namespace to prevent collisions.
	 */
	shutdownList: {
		componentsResponded: string[];
		waitFor: string[];
		componentsOpenAtShutdown: string[];
	} = {
		//This will be populated with the number of components that have told the Launcher whether they will require time to cleanup.
		componentsResponded: [],
		// This will be populated with the components who are doing some cleanup.
		waitFor: [],
		componentsOpenAtShutdown: [],
	};

	RouterClient: IRouterClient | undefined;

	groupStore: object | undefined;

	finsembleConfig: FinsembleConfig;

	// The initial manifest directly from FEA. This does not include all config imports.
	initialManifest: Manifest = {};

	// The full manifest from config service. This includes all config imports.
	fullManifest: Manifest = {};

	// Note that pending windows only contains properties that are used, does not contain actual windows.
	pendingWindows: Record<string, PendingWindow> = {};

	pendingSingletonWindows: Record<string, Promise<void>> = {};

	originalVisibilityValueMap: object = {};

	pendingEvergreenWindows: Array<string> = [];

	monitorsInstance: Monitors;

	doingMonitorAdjustments: boolean = false;

	dockingMain: DockingMain;

	launchGroupCounter: number = 0;

	isWindowsOs: boolean = true;

	store: WindowServiceStoreInstance;

	constructor(
		manifest: Manifest,
		monitorsInstance: Monitors,
		dockingMain: DockingMain,
		store: WindowServiceStoreInstance
	) {
		self = this;
		Logger.system.log("Starting Launcher");

		RouterClient.addPubSubResponder("Launcher.update", { State: "start" });

		this.store = store;
		this.monitorsInstance = monitorsInstance;
		this.dockingMain = dockingMain;
		this.createSplinterAndInject = new CreateSplinterAndInject(manifest);
		this.finsembleConfig = manifest.finsemble;
		this.initialManifest = manifest;

		// Removes sequential names queued up last time the app was open. We use local storage to remember how many toolbars we've spawned.
		this.clearSequentialNames(); // invoke on startup

		this.interopAdmin = new InteropAdmin();
	}

	/**
	 * Main function that starts everything up.
	 * @param {*} callback
	 */
	async initialize() {
		Logger.system.debug("Launcher.initialize");

		this.startHeartbeatMonitor();

		// When we wake from sleep, our heartbeats might be stale. Reset them.
		System.addEventListener("session-changed", this.resetHeartbeats);
		onMonitorsChanged(this.doMonitorAdjustments.bind(this));
		await this.getInitialManifest();
		await this.getConfig();
		this.isWindowsOs = await isWindows();
		this.loadComponents(this.finsembleConfig.components ?? {});
		Logger.system.debug("Launcher ready");
		this.getMonitorInfoAll((err, monitors) => {
			Logger.system.debug("Launcher monitors", monitors);
			RouterClient.addPubSubResponder("monitorInfo", monitors);
		});

		/**
		 * Responder for query in configService that signals that new components were
		 * added to the configuration and that the launcher needs update its component
		 * definition
		 */
		RouterClient.addResponder("launcher.loadComponents", (err, message) => {
			const newComponents = message?.data;
			Logger.system.debug("Launcher - launcher.loadComponents - responder", newComponents);
			this.loadComponents(newComponents);

			// Send the updated component list to any listeners (e.g. AppLauncher, interop service)
			RouterClient.publish("Launcher.update", {
				componentList: Components,
			});

			message?.sendQueryResponse(null);
		});

		RouterClient.addResponder("Finsemble.Launcher.SpawnEvergreenList", async (err, message) => {
			if (message && !RouterClient.trustedMessage(message)) {
				return message.sendQueryResponse(
					"SpawnEvergreenList failed. Only trusted (root domain) sources can spawn evergreen lists."
				);
			}
			const windowsSpawnParams = message?.data;
			Logger.system.debug("Launcher - launcher.spawnEvergreenList - responder", windowsSpawnParams);
			const namesMap = await this.spawnEvergreenList(windowsSpawnParams);
			message?.sendQueryResponse(null, namesMap);
		});
	}

	/**
	 * This method gets called from native-library applications by the following
	 * AssimilationService("Assimilation.Register") -> WindowService("FinsembleNewNativeWindow")
	 * This method makes sure the window service is aware of the window for docking, wrapping,
	 * and other management.
	 */
	async addNewNativeWindow({
		appUUID,
		name,
		windowType,
		options,
		appName,
	}: {
		appUUID: string;
		name: string;
		windowType: string;
		options: {};
		appName?: string;
	}) {
		if (!appUUID && !name && !appName) {
			Logger.system.error("Launcher.addNewNativeWindow called without valid window identification");
			return;
		}

		Logger.system.info("Launcher.addNewNativeWindow", Object.entries({ name, windowType, appUUID, appName }));

		const wrap: FSBLWindow = await this.createSplinterAndInject.addNewNativeWindow({
			appUUID,
			name,
			windowType,
			options,
			appName,
		});

		// Ensure the store has the WindowDescriptor before we actually spawn the window
		this.store.dispatch(
			actions.addLaunchData({
				windowDescriptor: wrap.windowDescriptor,
				workspaceWindow: false,
				finsembleConfig: this.finsembleConfig,
				windowDotOpen: true,
			})
		);

		activeWindows.addWindow(wrap);
		wrap.addEventListener("closed", this.remove);
	}

	/**
	 * This method gets called when someone calls `window.open`. We capture an event bubbled up from
	 * FEA, and then 'appropriate' that window so that all of the finsemble stuff will work with it.
	 * This method makes sure the window can participate in finsemble things. Without a wrap that
	 * the windowService knows of, it won't be able to do anything.
	 */
	async addExternallyOpenedWindow({
		appUUID,
		name,
		windowType = "WebWindow",
		options,
		appName,
	}: {
		appUUID: string;
		name: string;
		windowType: string;
		options: {};
		appName?: string;
	}) {
		Logger.system.debug("Launcher.addExternallyOpenedWindow", name, windowType, appUUID);
		// Ensure the store has the WindowDescriptor before we actually spawn the window
		this.store.dispatch(
			actions.addLaunchData({
				windowDescriptor: {
					name,
					...options,
				},
				workspaceWindow: false,
				finsembleConfig: this.finsembleConfig,
				windowDotOpen: true,
			})
		);

		const wrap: FSBLWindow = await this.createSplinterAndInject.addExternallyOpenedWindow({
			appUUID,
			name,
			windowType,
			options,
			appName,
		});
		activeWindows.addWindow(wrap);
		wrap.addEventListener("closed", this.remove);
	}

	/**
	 * This method handles the shutdownList sequence for the Launcher.
	 */
	shutdown(allDone: () => void) {
		return new Promise<void>((resolve) => {
			// bool to prevent new components from being spawned.
			self.shuttingDown = true;
			const smallTimeout = (callback) => {
				// I think I put this timeout in to allow the IPC to catch up. It may not be necessary. Leaving it in until we have time to try pulling it out.
				setTimeout(callback, 1);
			};

			asyncSeries([self.shutdownComponents.bind(this), smallTimeout], (err) => {
				if (err) {
					Logger.system.error("shutdown error", err);
				}
				allDone();
				// 'resolve' will resolve the shutdownList, which then calls shutdownComplete in the baseService.
				resolve();
			});
		});
	}

	// returns list of known components
	getComponents(): Record<string, any> {
		return Components;
	}

	// Adds windows to several groups.
	addWindowsToGroups(params: { groupName: string; windowList: string[] }) {
		let err: string | null = null;
		const { groupName, windowList } = params;
		if (self.windowGroups[groupName]) {
			const actualWindowList = self.getWindowsFromNamesOrIdentifiers(windowList);
			self.windowGroups[groupName].addWindows(actualWindowList);
			self.sendUpdatesToWindows(actualWindowList);
		} else {
			err = "Group Does Not Exist";
		}
		return err;
	}

	// return list of window names for a give group name
	getWindowsInGroup(groupName: string) {
		return self.windowGroups[groupName];
	}

	removeWindowsFromGroup(params: { groupName: string; windowList: string[] }) {
		let err: string | null = null;
		const { groupName, windowList } = params;
		if (self.windowGroups[groupName]) {
			self.windowGroups[groupName].removeWindows(windowList);
			const actualWindowList = self.getWindowsFromNamesOrIdentifiers(windowList);
			self.sendUpdatesToWindows(actualWindowList);
		} else {
			err = "Group Does Not Exist";
		}
		return err;
	}

	// Adds a window to several groups. This is deprecated.
	addWindowToGroups(data: { windowIdentifier: WindowIdentifier; groupNames?: string[] }) {
		Logger.system.debug("Add Window To Groups", data.groupNames, data.windowIdentifier);
		const win = activeWindows.getWindow(data.windowIdentifier.windowName);
		if (win) {
			for (const g in data.groupNames) {
				const groupName = data.groupNames[g];
				let group = self.windowGroups[groupName];
				if (!group) {
					self.windowGroups[groupName] = new LauncherWindowGroup({
						name: groupName,
					});
					group = self.windowGroups[groupName];
				}
				group.addWindow(win);
			}
			self.sendUpdatesToWindows([win]);
		}
	}

	/**
	 * Returns staggerPixels value.
	 */
	private getStaggerPixels(launcherParams: AdjustedSpawnParams): number {
		let staggerPixels;
		// In order we will check the local launcherParams.staggerPixels. If that is undefined, we will
		// check the this.finsembleConfig, if _that_ is undefined, we default to 40. (This magic number was here previously.)
		if (launcherParams.hasOwnProperty("staggerPixels") && !Number.isNaN(Number(launcherParams.staggerPixels))) {
			//eslint-disable-line
			({ staggerPixels } = launcherParams);
		} else if (
			this.finsembleConfig.servicesConfig &&
			this.finsembleConfig.servicesConfig.hasOwnProperty("launcher") &&
			this.finsembleConfig.servicesConfig.launcher.hasOwnProperty("staggerPixels")
		) {
			({ staggerPixels } = this.finsembleConfig.servicesConfig.launcher);
		} else {
			staggerPixels = 40;
		}
		return staggerPixels;
	}

	/**
	 * Returns the viewPort data. The viewport is a box that is identified by coordinates in the virtual space (all monitors) left, right, top, bottom calculations are done in that space
	 */
	private getViewData(params) {
		const { position, monitor, monitors, previousX, previousY, previousWidth, previousHeight } = params;
		let viewport;
		if (position === "available") {
			viewport = monitor.availableRect;
		} else if (position === "monitor") {
			viewport = monitor.monitorRect;
		} else if (position === "relative") {
			viewport = {
				left: previousX,
				top: previousY,
				right: previousX + parseInt(previousWidth?.toString()),
				bottom: previousY + parseInt(previousHeight?.toString()),
				width: previousWidth,
				height: previousHeight,
			};
		} else if (position === "virtual") {
			let virtualLeft = 0;
			let virtualTop = 0;
			let virtualWidth = 0;
			let virtualHeight = 0;
			monitors.forEach((innerMonitor) => {
				const dims = innerMonitor.availableRect;
				virtualWidth = virtualWidth + Math.abs(dims.right - dims.left);
				virtualHeight = virtualHeight + Math.abs(dims.bottom - dims.top);
				if (dims.left < virtualLeft) {
					virtualLeft = dims.left;
				}
				if (dims.top < virtualTop) {
					virtualTop = dims.top;
				}
			});

			viewport = {
				left: virtualLeft,
				top: virtualTop,
				width: virtualWidth,
				height: virtualHeight,
			};
		} else {
			viewport = monitor.availableRect;
		}
		return viewport;
	}

	/**
	 * This allows us to add a component that isn't in our list. We use the default manifest config so that our configs are the same across the board.
	 * We also allow these components to be included in workspaces.
	 * @param {*} message
	 * @param {*} cb
	 */
	addUserDefinedComponent(message: RouterMessage, cb = Function.prototype) {
		const { name } = message.data;
		const config = new LauncherDefaults().componentDescriptor;
		// Add in our configs to the default config
		config.window.url = message.data.url;
		config.window.windowType = message.data.windowType;
		// Sets the titlebar name to the user defined app name
		config.foreign.components["Window Manager"].title = name;
		// Allows us to know how this component was created. Eventually, this should change when we add a source to our components
		config.component.isUserDefined = true;
		config.component.type = name;

		let err: string | null = null;
		if (Components[name]) {
			err = `Component of type ${name} already exists.`;
		} else {
			this.addComponent(name, config);
		}
		this.update();
		cb(err, null);
	}

	private getWidthAndHeight(launcherParams, position, viewport) {
		// Width & height default to the component defaults, which is set earlier in the stack
		let width = 800;
		let height = 600;

		if (launcherParams.width || launcherParams.width === 0) {
			if (position !== "absolute" && util.isPercentage(launcherParams.width)) {
				width = (viewport.width * parseFloat(launcherParams.width.toString())) / 100;
			} else {
				width = parseFloat(launcherParams.width.toString());
			}
		}

		if (launcherParams.height || launcherParams.height === 0) {
			if (position !== "absolute" && util.isPercentage(launcherParams.height)) {
				height = (viewport.height * parseFloat(launcherParams.height.toString())) / 100;
			} else {
				height = parseFloat(launcherParams.height.toString());
			}
		}
		return { width, height };
	}

	/**
	 * Returns an updated set of window coordinates with an emphasis on the left parameters
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private getLeftPlus(params) {
		const {
			staggerPixels,
			leftCommand,
			rightCommand,
			monitor,
			viewport,
			width,
			previousX,
			previousWidth,
			lastOpened,
			launcherParams,
		} = params;
		let { left, updateX, lastX, lastY } = params;

		if (leftCommand === "center") {
			const center = viewport.left + viewport.width / 2;
			left = center - width / 2;
		} else if (leftCommand === "adjacent") {
			left = previousX + parseInt(previousWidth.toString());
		} else if (leftCommand === "aligned") {
			left = previousX;
		} else if (leftCommand || leftCommand === 0) {
			if (util.isPercentage(leftCommand)) {
				left = viewport.left + (viewport.width * parseFloat(leftCommand.toString())) / 100;
			} else {
				left = viewport.left + parseFloat(leftCommand.toString());
			}
		} else if (!rightCommand && rightCommand !== 0) {
			// Make sure last opened window was on our viewport.
			if (!(lastX >= monitor.availableRect.left && lastX <= monitor.availableRect.right)) {
				lastX = monitor.availableRect.left;
				lastY = monitor.availableRect.top;
			}
			if (isNaN(lastX)) {
				lastX = null;
			}
			// stagger if neither left nor right commands
			if (launcherParams.relativeWindow) {
				lastOpened.x = previousX;
			} else if (lastX === null) {
				// start at 0
				lastX = monitor.availableRect.left - staggerPixels;
			}
			left = lastX + staggerPixels;
			// Make sure we don't go off right edge of monitor
			if (left + width > monitor.availableRect.right) {
				left = monitor.availableRect.right - width;
			}
			updateX = true;
		}
		return { left, updateX, lastOpened, lastX, lastY };
	}

	/**
	 * Returns an updated set of window coordinates with an emphasis on the right parameters
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private getRightPlus(params) {
		const { viewport, rightCommand, previousWidth, previousX } = params;
		let { left, width, right } = params;
		if (rightCommand === "adjacent") {
			left = previousX - width;
		} else if (rightCommand === "aligned") {
			left = previousX + parseInt(previousWidth.toString()) - width;
		} else if (rightCommand || rightCommand === 0) {
			if (util.isPercentage(rightCommand)) {
				right = viewport.right - (viewport.width * parseFloat(rightCommand.toString())) / 100;
			} else {
				right = viewport.right - parseFloat(rightCommand.toString());
			}
			if (left || left === 0) {
				// If we have a left command and right command, then set the width
				width = right - left;
			} else {
				// If we only have a right command and a width, then we back into the left
				left = right - width;
			}
		}
		return { left, right, width };
	}

	/**
	 * Returns an updated set of window coordinates with an emphasis on the top parameters
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private getTopPlus(params) {
		const {
			launcherParams,
			lastOpened,
			staggerPixels,
			topCommand,
			previousY,
			viewport,
			monitor,
			height,
			previousHeight,
			bottomCommand,
		} = params;
		let { top, updateY, lastX, lastY } = params;

		if (topCommand === "center") {
			const center = viewport.top + viewport.height / 2;
			top = center - height / 2;
		} else if (topCommand === "adjacent") {
			top = previousY + parseInt(previousHeight.toString());
		} else if (topCommand === "aligned") {
			top = previousY;
		} else if (topCommand || topCommand === 0) {
			if (util.isPercentage(topCommand)) {
				top = viewport.top + (viewport.height * parseFloat(topCommand.toString())) / 100;
			} else {
				top = viewport.top + parseFloat(topCommand.toString());
			}
		} else if (!bottomCommand && bottomCommand !== 0) {
			// Make sure last opened window was on our viewport.
			if (!(lastY <= monitor.availableRect.bottom && lastY >= monitor.availableRect.top)) {
				lastY = monitor.availableRect.top;
				lastX = monitor.availableRect.left;
			}
			if (isNaN(lastY)) {
				lastY = null;
			}
			// stagger
			if (launcherParams.relativeWindow) {
				lastOpened.y = previousY;
			} else if (lastY === null) {
				// start at 0
				lastY = monitor.availableRect.top - staggerPixels;
			}
			top = lastY + staggerPixels;
			// Make sure we don't go off right edge of monitor
			if (top + height > monitor.availableRect.bottom) {
				top = monitor.availableRect.bottom - height;
			}
			updateY = true;
		}
		return { top, lastX, lastY, lastOpened, updateY };
	}

	/**
	 * Returns an updated set of window coordinates with an emphasis on the bottom parameters
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private getBottomPlus(params) {
		const { viewport, previousY, bottomCommand, previousHeight } = params;
		let { bottom, top, height } = params;
		if (bottomCommand === "adjacent") {
			top = previousY - height;
		} else if (bottomCommand === "aligned") {
			top = previousY + parseInt(previousHeight.toString()) - height;
		} else if (bottomCommand || bottomCommand === 0) {
			if (util.isPercentage(bottomCommand)) {
				bottom = viewport.bottom - (viewport.height * parseFloat(bottomCommand.toString())) / 100;
			} else {
				bottom = viewport.bottom - parseFloat(bottomCommand.toString());
			}
			if (top || top === 0) {
				height = bottom - top;
			} else {
				top = bottom - height;
			}
		}
		return { bottom, top, height };
	}

	/**
	 * Based on input, update a set of window coordinates to potential force a window on to a specific monitor
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private potentiallyForceOnMonitor(params) {
		const { topCommand, monitor, previousY, height, width } = params;
		let { launcherParams, left, right, bottom, top } = params;

		if (launcherParams.forceOntoMonitor !== false) {
			// forceOntoMonitor _can_ be set to "monitorRect" (instead of boolean). This is done in MenuPortal.tsx
			const whichRect = launcherParams.forceOntoMonitor === "monitorRect" ? "monitorRect" : "availableRect";
			const rect = monitor[whichRect];
			if (right > rect.right) {
				left = left - (right - rect.right);
				({ right } = rect);
			}

			if (bottom > rect.bottom) {
				// Before, the bottom of the window being shown would line up with the bottom of the monitor. If this was a menu, this was problematic because the menu would overlap the toolbar. Instead, we line up the bottom of the window with the top of the relative window.
				if (topCommand === "adjacent") {
					bottom = previousY;
					top = bottom - height;
				} else {
					top = top - (bottom - rect.bottom);
				}
			}

			// left after right in case window bigger than viewport
			if (left < rect.left) {
				({ left } = rect);
				right = left + width;
			}

			if (top < rect.top) {
				({ top } = rect);
				bottom = top + height;
			}
		}
		return { launcherParams, left, right, bottom, top };
	}

	/**
	 * Returns a set of new window coordinates based on various input
	 *
	 * @private
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private getNewWindowCoordinates(params) {
		const { monitor, position, viewport, staggerPixels, previousWidth, previousX, previousY, previousHeight } = params;
		let { launcherParams } = params;

		let { width, height } = this.getWidthAndHeight(launcherParams, position, viewport);

		// Various x,y placement commands are possible.
		let leftCommand = launcherParams.left;
		let topCommand = launcherParams.top;
		const rightCommand = launcherParams.right;
		const bottomCommand = launcherParams.bottom;
		let left;
		let right;
		let top;
		let bottom;
		let updateX;
		let updateY;

		// Initialize the lastOpenedMap if not already. First window will open in top left corner of screen. This
		// only gets updated when a window is opened without any specific location. The entire thing resets if the user
		// hasn't opened a window in over a minute.
		const resetStaggerTimer = 1000 * 60;
		let lastOpened = self.lastOpenedMap[monitor.position ?? 0];
		if (!lastOpened || Date.now() - lastOpened.then > resetStaggerTimer) {
			self.resetSpawnStagger({ monitorPosition: monitor.position ?? 0 });
			lastOpened = self.lastOpenedMap[monitor.position ?? 0];
		}
		if (position === "absolute") {
			({ left, right, top, bottom } = launcherParams);
		} else {
			// For "adjacent" we want to automatically align the new component (unless specified otherwise by the developer)
			const leftRightAdjacent = leftCommand === "adjacent" || rightCommand === "adjacent";
			const topBottomAdjacent = topCommand === "adjacent" || bottomCommand === "adjacent";
			if (leftRightAdjacent && !topCommand && topCommand !== 0) {
				topCommand = "aligned";
			} else if (topBottomAdjacent && !leftCommand && leftCommand !== 0) {
				leftCommand = "aligned";
			}

			let lastY = lastOpened.y;
			let lastX = lastOpened.x;
			({ left, updateX, lastOpened, lastX, lastY } = this.getLeftPlus({
				staggerPixels,
				leftCommand,
				rightCommand,
				monitor,
				viewport,
				width,
				previousX,
				previousWidth,
				lastOpened,
				launcherParams,
				left,
				updateX,
				lastX,
				lastY,
			}));

			({ right, left, width } = this.getRightPlus({
				left,
				viewport,
				rightCommand,
				width,
				previousWidth,
				previousX,
				right,
			}));

			({ top, lastX, lastY, lastOpened, updateY } = this.getTopPlus({
				launcherParams,
				lastOpened,
				staggerPixels,
				topCommand,
				previousY,
				viewport,
				monitor,
				height,
				previousHeight,
				bottomCommand,
				top,
				updateY,
				lastX,
				lastY,
			}));

			({ bottom, top, height } = this.getBottomPlus({
				viewport,
				previousY,
				bottomCommand,
				previousHeight,
				bottom,
				top,
				height,
			}));
		}

		// Make sure we have a right and a bottom
		if (!right && Number.isFinite(left)) {
			right = left + width;
		}
		if (!bottom && Number.isFinite(top)) {
			bottom = top + height;
		}

		({ launcherParams, left, right, bottom, top } = this.potentiallyForceOnMonitor({
			topCommand,
			monitor,
			previousY,
			height,
			width,
			launcherParams,
			left,
			right,
			bottom,
			top,
		}));

		// Only if both x and y were unassigned do we save the information so that we can stagger again later
		if (updateX && updateY) {
			if (launcherParams.options && typeof launcherParams.options.defaultLeft === "undefined") {
				// defaultLeft is set when being restored from workspace. We don't want those to affect the stagger algorithm.
				Logger.system.debug("lastOpened.x", left, launcherParams);
				lastOpened.x = left;
				lastOpened.y = top;
			}
			lastOpened.then = Date.now();
		}

		return { left, top, width, height, leftCommand, rightCommand, topCommand, bottomCommand, lastOpened };
	}

	/**
	 * Returns previous-coordinate data
	 *
	 * @private
	 * @param {*} launcherParams
	 * @param {*} monitor
	 * @returns
	 * @memberof Launcher
	 */
	private getPreviousData(launcherParams, monitor) {
		// Set variables for calculations based on the dimensions of the opening window
		const { previousWindowBounds } = launcherParams;
		const previousX = previousWindowBounds ? launcherParams.previousWindowBounds.left : monitor.availableRect.left;
		const previousY = previousWindowBounds ? launcherParams.previousWindowBounds.top : monitor.availableRect.top;
		const previousWidth = previousWindowBounds ? previousWindowBounds.width : (launcherParams.width ?? 40);
		const previousHeight = previousWindowBounds ? previousWindowBounds.height : (launcherParams.height ?? 40);
		return { previousX, previousY, previousWidth, previousHeight };
	}

	/**
	 * Returns an updated window descriptor
	 *
	 * @private
	 * @param {*} windowDescriptor
	 * @param {*} params
	 * @returns
	 * @memberof Launcher
	 */
	private updateWindowDescriptor(windowDescriptor, params) {
		const { left, top, width, height, monitor, launcherParams } = params;
		if (left || left === 0) {
			windowDescriptor.defaultLeft = Math.round(left);
		}
		if (top || top === 0) {
			windowDescriptor.defaultTop = Math.round(top);
		}
		if (width || width === 0) {
			windowDescriptor.defaultWidth = Math.round(width);
		}
		if (height || height === 0) {
			windowDescriptor.defaultHeight = Math.round(height);
		}
		windowDescriptor.monitorInfo = monitor.position; // only this position is used elsewhere so stop putting the entire object in the windowDescriptor so as not to overload the store

		if (typeof launcherParams.claimMonitorSpace !== "undefined") {
			windowDescriptor.claimMonitorSpace = launcherParams.claimMonitorSpace;
		}

		if (launcherParams.forceOntoMonitor) {
			windowDescriptor = this.adjustWindowDescriptorBoundsToBeOnMonitor(
				windowDescriptor,
				launcherParams.forceOntoMonitor
			);
		}
		return windowDescriptor;
	}

	/**
	 * Calculate window bound values based on launch parameters plus other class variables
	 *
	 * @param {MonitorInfoDetail} foundMonitor
	 * @param {WindowDescriptor} windowDescriptor
	 * @param {AdjustedSpawnParams} launcherParams
	 * @returns {Promise<WindowDescriptor>}
	 * @memberof Launcher
	 */
	calculateBounds(
		foundMonitor: MonitorInfoDetail,
		windowDescriptor: WindowDescriptor,
		launcherParams: AdjustedSpawnParams
	): Promise<WindowDescriptor> {
		const { position, monitors, previousMonitor } = launcherParams;
		let monitor = previousMonitor;

		// Client can optionally override by picking a monitor
		const commandMonitor = launcherParams.monitor;

		if ((commandMonitor && commandMonitor !== "mine") || commandMonitor === 0) {
			monitor = foundMonitor;
		}

		if (!monitor) monitor = foundMonitor;

		// If this is an ephemeral component don't update the lastOpenedMap
		let shouldUpdateLastOpened = !launcherParams.ephemeral;

		// Set monitorDimensions since other services reference this.
		if (!windowDescriptor.customData) {
			windowDescriptor.customData = {};
		}

		// just in case we don't send an actual windowDescriptor in
		windowDescriptor.customData.monitorDimensions = monitor.availableRect;

		// Set variables for calculations based on the dimensions of the opening window
		const { previousX, previousY, previousWidth, previousHeight } = this.getPreviousData(launcherParams, monitor);

		// get how much to stagger window position from previously launched window
		let staggerPixels = this.getStaggerPixels(launcherParams);

		// The viewport is a box that is identified by coordinates in the virtual space (all monitors)
		// left, right, top, bottom calculations are done in that space
		let viewport = this.getViewData({
			position,
			monitor,
			monitors,
			previousX,
			previousY,
			previousWidth,
			previousHeight,
		});

		const { left, top, width, height, leftCommand, rightCommand, topCommand, bottomCommand, lastOpened } =
			this.getNewWindowCoordinates({
				launcherParams,
				monitor,
				position,
				viewport,
				staggerPixels,
				previousWidth,
				previousX,
				previousY,
				previousHeight,
			});

		windowDescriptor = this.updateWindowDescriptor(windowDescriptor, {
			left,
			top,
			width,
			height,
			monitor,
			launcherParams,
		});

		// If a leftCommand/rightCommand/topCommand/bottomCommand
		// or position other than 'virtual' is supplied then don't update the lastOpenedMap.
		// This can also be skipped if shouldUpdateLastOpened is already false
		// (like when the opening window is a menu)
		// NOTE: position virtual is an acceptable value to update the lastOpenedMap since its just the entire virtual usable space
		if (
			shouldUpdateLastOpened &&
			(topCommand || bottomCommand || leftCommand || rightCommand) &&
			(!position || position !== "virtual")
		) {
			shouldUpdateLastOpened = false;
		}

		// If the launching window is a menu we don't want to update this map, otherwise the next window will stagger will just open on top of the last window (since the menu's location won't change in most cases)
		if (shouldUpdateLastOpened) {
			this.lastOpenedMap[monitor.position ?? 0] = {
				x: windowDescriptor.defaultLeft,
				y: windowDescriptor.defaultTop,
				then: lastOpened.then ? lastOpened.then : undefined,
			}; // NOTE: 4/5/19 lastOpened.then was not being added to the lastOpenedMap, so the stagger timer was likely broken
		}

		return Promise.resolve(windowDescriptor);
	}

	/**
	 * Takes the window descriptor's bounds and makes sure it's on a monitor. If the window isn't on a monitor, we determine the closest monitor
	 * based on the distance from the top-left corner of the window to the center of the monitor, and then pull the monitor along that line
	 * until the window is on the edge of the monitor
	 * @param {*} windowDescriptor Window descriptor, e.g. from a saved workspace
	 * @param {*} previousWindowBounds not used, unfortunately
	 * @returns windowDescriptor updated window descriptor
	 */
	adjustWindowDescriptorBoundsToBeOnMonitor(
		windowDescriptor: WindowDescriptor,
		rectToUse: string = "availableRect"
	): WindowDescriptor {
		if (windowDescriptor?.customData?.window?.allowToSpawnOffScreen) {
			return windowDescriptor;
		}
		let bounds;
		/** Currently our bounds data (top, left, height, width, etc.) is duplicated in up to three places
		 * in the WindowDescriptor object: as top level props (windowDescriptor.left), as top level
		 * props with a "default" prefix (windowDescriptor.defaultLeft), and, in the case of StackedWindows
		 * (and in this case only), within the "bounds" prop (windowDescriptor.bounds.left).
		 */
		bounds = {
			left: windowDescriptor.defaultLeft,
			top: windowDescriptor.defaultTop,
			height: windowDescriptor.defaultHeight,
			width: windowDescriptor.defaultWidth,
			right: null,
			bottom: null,
		};

		bounds.right = bounds.left + bounds.width;
		bounds.bottom = bounds.top + bounds.height;

		if (windowDescriptor.customData && windowDescriptor.customData.monitorDimensions) {
			const newBounds = adjustBoundsToBeOnMonitor(this.monitorsInstance, bounds, rectToUse);

			// update windowDescriptor
			windowDescriptor.defaultLeft = newBounds.left;
			windowDescriptor.defaultTop = newBounds.top;
			windowDescriptor.left = newBounds.left;
			windowDescriptor.top = newBounds.top;
			windowDescriptor.height = newBounds.height;
			windowDescriptor.width = newBounds.width;
			windowDescriptor.right = newBounds.left + newBounds.width;
			windowDescriptor.bottom = newBounds.top + newBounds.height;
		}
		return windowDescriptor;
	}

	// clears counters from local storage -- counters will restart at 1 for new names
	clearSequentialNames() {
		localStorage.removeItem(NAME_STORAGE_KEY);
	}

	private updateEphemeralSettings(ephemeral, windowDescriptor, config) {
		if (ephemeral) {
			windowDescriptor.resizable = false;
			windowDescriptor.showTaskbarIcon = false;
			windowDescriptor.alwaysOnTop = true;
			// ephemeral objects shouldn't be added to the workspace, unless explicitly set in their config
			if (typeof config.window.addToWorkspace === "undefined") {
				config.window.addToWorkspace = false;
			}
		}
	}

	private updateOptionSettings(options, windowDescriptor, config) {
		if (options) {
			const overwriteMerge = (destinationArray, sourceArray) =>
				_.uniqWith(destinationArray.concat(sourceArray), _.isEqual);

			windowDescriptor = merge(windowDescriptor, options, {
				arrayMerge: overwriteMerge,
			});
			// If the component is unknown, we must make sure it has the proper URL for the
			// unknown component. If the component is native, the url is removed by the merge from
			// params.options (params.options.window does not have a url...).
			// We delete the windowType on both objects so that WPF/Native applications
			// are spawned as unknown Web applications. Otherwise, the spawn requests
			// go off into the ether and prevent workspaces from loading properly.
			if (config.component && config.component.isUnknownComponent) {
				windowDescriptor.url = config.window.url;
				delete windowDescriptor.windowType;
				delete windowDescriptor.customData?.window.windowType;
			}
		}
		return windowDescriptor;
	}

	private updateSpawnDataSettings(data, windowDescriptor) {
		// Any data passed by argument is added to spawnData so that it's available to the new window
		if (data) {
			windowDescriptor.customData = {
				...windowDescriptor.customData,
				spawnData: data,
			};
		}
	}

	private updatePreloadSettings(windowDescriptor, config) {
		// Add the titlebar to the preload list if BrowserView is disabled and if the component supports FSBLHeader
		// The browser view can be disabled globally by setting titlebarType to "injected" in the "Window Manager"
		// configuration. Or, it can be disabled for an individual component by setting the same value in the foreign
		// section for that component.
		const titlebarType = _determineTitleBarType(this.finsembleConfig, config, windowDescriptor.componentType);

		// Check for customData.window.compound
		const isCompoundWindow = get(windowDescriptor, "customData.window.compound", false);
		// Check for customData.Window Manager.FSBLHeader
		const componentSupportsHeader =
			!isCompoundWindow &&
			get(windowDescriptor, ["customData", "foreign", "components", "Window Manager", "FSBLHeader"], true);

		const url = windowDescriptor.windowTitleBarUrl;

		if (titlebarType === "injected" && componentSupportsHeader) {
			// push into the preloadScripts array
			if (windowDescriptor.preloadScripts?.findIndex((obj) => obj.url === url) === -1) {
				windowDescriptor.preloadScripts.push({ url: url });
			}

			// preload is a shallow copy of preloadScripts but when loaded from another workspace they can point to different addresses in memory
			if (windowDescriptor.preload?.findIndex((obj) => obj.url === url) === -1) {
				windowDescriptor.preload.push({ url: url });
			}
		} else {
			if (windowDescriptor.preload && url) {
				const titleBarPreload = windowDescriptor.preload.findIndex(
					(obj) => obj.url.toLowerCase() === url.toLowerCase()
				);
				if (titleBarPreload !== -1) {
					windowDescriptor.preload.splice(titleBarPreload, 1);
				}
			}
		}
	}

	private updateURLPathSettings(params, windowDescriptor, config) {
		if (params.spawnedByWorkspaceService) {
			const persistURL =
				config?.foreign?.services?.workspaceService?.persistURL ??
				config?.foreign?.services?.workspace?.persistURL ??
				this.persistURL;

			const persistPath =
				config?.foreign?.services?.workspaceService?.persistPath ??
				config?.foreign?.services?.workspace?.persistPath ??
				this.persistPath;

			const persistArguments =
				config?.foreign?.services?.workspaceService?.persistArguments ??
				config?.foreign?.services?.workspace?.persistArguments ??
				this.persistArguments;

			/**
			 * We store the fact that a component had its URL swapped with the unknown component URL
			 * on the windowDescriptor itself. Therefore, if that prop is true, we need to swap the
			 * current URL with the one found in config. This logic will likely need to remain here
			 * (where we have access to the component's config), regardless of where the persistURL
			 * logic lands. */
			const isUnknownComponent = get(params, "options.customData.component.isUnknownComponent");
			if (!persistURL || isUnknownComponent) {
				windowDescriptor.url = config.window.url;
			}
			if (!persistPath || isUnknownComponent) {
				windowDescriptor.path = config.window.path;
			}
			if (!persistArguments || isUnknownComponent) {
				windowDescriptor.arguments = config.window.arguments;
			}
		}
	}

	/**
	 * @private
	 */
	async compileWindowDescriptor(
		config: ComponentConfig,
		params: AdjustedSpawnParams,
		baseDescriptor: WindowDescriptor,
		resultFromDeriveBounds: WindowDescriptor
	): Promise<WindowDescriptor> {
		let windowDescriptor = baseDescriptor;

		// for ephemeral windows, such as dialogs, menus, linker, etc
		this.updateEphemeralSettings(params.ephemeral, windowDescriptor, config);

		// Getting the system-wide always on top mode
		if (windowDescriptor.alwaysOnTop) {
			/**
			 * Electron AoT default level is "floating" (null). "floating" causes an issue where always on top hides the
			 * window behind all other windows when screen sharing. On Windows we use "normal" to avoid this problem
			 */
			const { data: alwaysOnTopLevel } = await ConfigClient.get([
				"finsemble",
				"servicesConfig",
				"docking",
				"alwaysOnTopLevel",
			]);
			const defaultLevel = (await isWindows()) ? "normal" : null;
			windowDescriptor.alwaysOnTopLevel = alwaysOnTopLevel ?? defaultLevel;
		}

		config.window = config.window ?? {};

		// Make sure affinity gets passed down to the container
		const globalAffinity = this.finsembleConfig?.servicesConfig?.launcher?.defaultComponentAffinity ?? null;
		// Affinity false is used to disable affinity, affinity true should not be used. Log an error then unset the value so it will
		// fallback to the next possible affinity value.
		// The union type for affinity is string | boolean instead of false because we can't protect against passed in params
		if (params.affinity === true) {
			Logger.system.error(
				"An invalid affinity setting was passed as a spawn argument. Affinity values should be strings or false",
				params
			);
			params.affinity = undefined;
		}
		if (config.window.affinity === true) {
			Logger.system.error(
				"An invalid affinity setting: true was passed in an application config. Affinity values should be strings or false",
				config
			);
			config.window.affinity = undefined;
		}
		// the priority for affinity is passed in parameter, local config, global config and finally the default set in the schema
		windowDescriptor.affinity = params.affinity ?? config.window.affinity ?? globalAffinity;

		// Override all settings with any "options" from the config
		if (config.window.options) {
			windowDescriptor = merge(windowDescriptor, config.window.options);
		}

		// Merging first so that any params that the dev passes in overwrite what we calculate.
		windowDescriptor = merge(windowDescriptor, resultFromDeriveBounds);

		// Add the config entries into customData so that it's available to the new window
		windowDescriptor.customData = merge(windowDescriptor.customData as any, config);

		if (params.compositeComponentData && !params.launchGroupComponentData) {
			// ensure backwards compatibility with previous composite component data
			params.launchGroupComponentData = {
				launchGroupComponentUUID: params.compositeComponentData.compositeComponentUUID,
				closeComponentsTogether: params.compositeComponentData.closeComponentsTogether ?? false,
			};
		}

		// If we have launchGroup component data, add it to the window descriptor
		if (params.launchGroupComponentData) {
			windowDescriptor.launchGroupComponentData = params.launchGroupComponentData;
		}

		this.updateSpawnDataSettings(params.data, windowDescriptor);

		windowDescriptor = this.updateOptionSettings(params.options, windowDescriptor, config);

		// Bounds correction in case of OS display re-arrangements
		const allowToSpawnOffScreen = get(windowDescriptor, "customData.window.allowToSpawnOffScreen");
		if (!allowToSpawnOffScreen && params.forceOntoMonitor !== false) {
			windowDescriptor = this.adjustWindowDescriptorBoundsToBeOnMonitor(windowDescriptor);
		}

		// @deprecated: REMOVE IN 7.0
		if (!windowDescriptor.execJSWhitelist) windowDescriptor.execJSWhitelist = [];
		windowDescriptor.execJSWhitelist.push(System.Window.getCurrent().name);

		// pass in custom data so router can use
		windowDescriptor.customData = {
			...windowDescriptor.customData,
			manifest: this.initialManifest,
		};
		Logger.system.debug("Launcher.compileWindowDescriptor", windowDescriptor);

		windowDescriptor.windowTitleBarUrl = deriveWindowTitleBarUrl({
			windowTitleBarUrl: params.windowTitleBarUrl,
			componentConfig: config,
			finsembleConfig: this.finsembleConfig,
		});

		this.updatePreloadSettings(windowDescriptor, config);

		windowDescriptor.icons = getIcons(this.fullManifest, config);

		this.updateURLPathSettings(params, windowDescriptor, config);

		// Make sure the URL of the windowDescriptor is set before you get the security policy. Otherwise the security policy
		// is going to set to the most restrictive ones for the component by default.
		windowDescriptor.securityPolicy = SpawnUtils.getSecurityPolicy(windowDescriptor, this.finsembleConfig);
		windowDescriptor.permissions = SpawnUtils.getPermissions(windowDescriptor, this.finsembleConfig);

		// Window must respect limits
		if (params.minHeight) {
			windowDescriptor.minHeight = params.minHeight;
		}
		if (params.maxHeight) {
			windowDescriptor.maxHeight = params.maxHeight;
		}
		if (params.minWidth) {
			windowDescriptor.minWidth = params.minWidth;
		}
		if (params.maxWidth) {
			windowDescriptor.maxWidth = params.maxWidth;
		}
		// windowDescriptor.childWindowOptions === baseDescriptor.childWindowOptions
		// which are the defaults. we overwrite with any configs that are passed in.
		if (config.childWindowOptions) {
			windowDescriptor.childWindowOptions = merge(
				windowDescriptor.childWindowOptions as any,
				config.childWindowOptions
			);
		}

		windowDescriptor.allowAutoArrange = config.foreign?.services?.windowService?.allowAutoArrange ?? true;

		return windowDescriptor;
	}

	/**
	 * Locates a window based on a componentType
	 * @param {object} windowIdentifier The parameters
	 * @param  {string}   windowIdentifier.componentType	 The type of component
	 * @return {finWindow} Returns a finWindow for the component, or null if not found
	 */
	componentFinder(windowIdentifier: WindowIdentifier) {
		if (!windowIdentifier.componentType) {
			Logger.system.error("componentFinder windowIdentifier.componentType is not defined.  Setting to empty string");
			windowIdentifier.componentType = "";
		}
		const windowsOfComponentType = activeWindows.findAllByComponentType(windowIdentifier.componentType);
		if (windowsOfComponentType.length) {
			return windowsOfComponentType[0];
		}
		return null;
	}

	/**
	 * Create Window Group
	 */
	createWindowGroup(err: StandardError, message: ResponderMessage | { data: any; sendQueryResponse?: Function }) {
		Logger.system.debug("Creating Group", err, message);
		if (!message.data.groupName && message.sendQueryResponse) {
			return message.sendQueryResponse("No Group name Specified");
		}
		// Build Window List from window names/identifiers
		const actualWindowList = self.getWindowsFromNamesOrIdentifiers(message.data.windowList, true);
		self.windowGroups[message.data.groupName] = new LauncherWindowGroup({
			name: message.data.groupName,
			windows: actualWindowList,
		});

		self.sendUpdatesToWindows(self.getWindowsFromNamesOrIdentifiers(message.data.windowList));

		if (message.sendQueryResponse) message.sendQueryResponse(null, "Success");
	}

	deleteWindowGroup(err: StandardError, message: ResponderMessage) {
		Logger.system.debug("Deleting Group", message.data.groupName);
		if (!message.data.groupName) {
			return message.sendQueryResponse("No Group name Specified");
		}
		const { groupName } = message.data;
		if (self.windowGroups[groupName]) {
			const group = self.windowGroups[groupName];
			const windowList = group.getWindows();
			group.destroy();
			delete self.windowGroups[message.data.groupName];
			self.sendUpdatesToWindows(windowList);
		}
		message.sendQueryResponse(null, "Success");
	}

	/**
	 * Sets the dimensions and placement of the window by translating the launcherParams
	 *
	 * @params	object	launcherParams Params from spawn()
	 * @returns {Promise} A promise that resolves to a new windowDescriptor that describes the new window.
	 * with defaultLeft, defaultTop, defaultWidth, defaultHeight, and claimMonitorSpace set.
	 */
	deriveBounds(
		launcherParams: SpawnParams
	): Promise<{ windowDescriptor: WindowDescriptor; adjustedSpawnParams: AdjustedSpawnParams }> {
		// clone the launcherParams for modification
		let adjustedLauncherParams: AdjustedSpawnParams = JSON.parse(JSON.stringify(launcherParams));

		const windowDescriptor: WindowDescriptor = {};
		// Default to same monitor of the relativeWindow passed in (usually the window that launched us)

		// Get windowDescriptor for the previous window (the caller or relativeWindow)
		function addPreviousWindow(params): Promise<any> {
			async function promiseResolver(resolve) {
				const whichWindow = params.relativeWindow || params.launchingWindow;
				if (whichWindow) {
					params.previousWindow = activeWindows.getWindow(whichWindow.windowName);
				}
				resolve(params);
			}
			return new Promise(promiseResolver);
		}

		// Get all monitors
		const addAllMonitors = async (params) => {
			const monitors = await getAllMonitors();
			params.monitors = monitors;
			return params;
		};

		// Get the monitor descriptor for that previous window
		function addPreviousMonitor(params) {
			return new Promise<any>(async (resolve) => {
				if (params.relativeWindowCachedBounds) {
					params.previousWindowBounds = params.relativeWindowCachedBounds;
					delete params.relativeWindowCachedBounds;
					const monitor = await getMonitorFromScaledXY(
						params.previousWindowBounds.left,
						params.previousWindowBounds.top
					);
					params.previousMonitor = monitor;
					resolve(params);
				} else if (params.previousWindow) {
					// as is this code on startup tried to get bounds of Workspace Service (i.e. the previousWindow).  This is a flawed way to determine
					// which monitor to start up....for one the Workspace service doesn't have a bounds after wrapper cleanup.  Short-term solution is
					// on error then set monitor[0] as the previousMonitor.
					params.previousWindow._getBounds(async (err, bounds) => {
						// bounds are undefined for Windowless WPF Components
						if (!err && bounds !== undefined) {
							params.previousWindowBounds = bounds;
							const monitor = await getMonitorFromScaledXY(bounds.left, bounds.top);
							params.previousMonitor = monitor;
							resolve(params);
						} else {
							params.previousMonitor = params.monitors[0];
							resolve(params);
						}
					});
				} else {
					resolve(params);
				}
			});
		}
		// Gets the monitor for the window that we're about to spawn.
		function getWhichMonitorForParams(params): Promise<MonitorInfoDetail> {
			return new Promise<any>(async (resolve) => {
				const getWhichMonitorParams = {
					commandMonitor: params.monitor,
					monitors: params.monitors,
					launchingMonitorPosition: params.position,
					windowIdentifier: (params.relativeWindow || params.launchingWindow) ?? { windowName: "undefined" },
					activeWindows,
				};
				const monitor = await getWhichMonitor(getWhichMonitorParams);
				resolve(monitor);
			});
		}

		// Add information to adjustedLauncherParams (previous window, monitors, etc).
		// when all is collected, call calculateBounds() to finish the job.
		const promiseResolver = async (resolve) => {
			adjustedLauncherParams = await addPreviousWindow(adjustedLauncherParams);
			adjustedLauncherParams = await addAllMonitors(adjustedLauncherParams);
			adjustedLauncherParams = await addPreviousMonitor(adjustedLauncherParams);
			const monitor = await getWhichMonitorForParams(adjustedLauncherParams);

			// If launcherParams supplied ephemeral no need to check, otherwise this request
			// may have come from 'showWindow' and need to get the components properties
			// from launcher. An 'ephemeral' window is most likely a menu and should
			// not update the lastOpenedMap
			if (
				!adjustedLauncherParams.hasOwnProperty("ephemeral") &&
				adjustedLauncherParams.windowIdentifier &&
				adjustedLauncherParams.windowIdentifier.componentType
			) {
				launcherParams.ephemeral = Components[adjustedLauncherParams.windowIdentifier.componentType].window.ephemeral;
			}

			const newWindowDescriptor = await this.calculateBounds(monitor, windowDescriptor, adjustedLauncherParams);

			resolve({ windowDescriptor: newWindowDescriptor, adjustedSpawnParams: adjustedLauncherParams });
		};

		return new Promise(promiseResolver);
	}

	/**
	 * The basic algorithm for handling monitor adjustments is:
	 * 1) Remove any orphaned components. These would be any spawnOnAllMonitor components that are now located
	 * on a different monitor than they started. We simply compare their existing monitor with the one they were
	 * spawned upon, and remove them if they aren't where they belong.
	 *
	 * 2) Add any motherless components. These would be any spawnOnAllMonitor components that are missing from a
	 * particular monitor, presumably because the monitor just got added.
	 *
	 * 3) Adjust component dimensions. Since the monitor size may have changed we need to adjust any components
	 * that had previously made assumptions about monitor size (such as a toolbar that is supposed to stretch across
	 * the top of the screen). We cycle through any components that have made a "claim" on monitor space and then
	 * simply call showWindow() with their original params in order to give them a chance to resettle.
	 */

	doMonitorAdjustments(changeData: { monitors: MonitorInfoDetail[] }) {
		const { monitors } = changeData;
		// Event fires multiple times for monitor changes - so stop things from happening too many times.
		if (this.doingMonitorAdjustments) return;
		this.doingMonitorAdjustments = true;
		const components = {};
		asyncSeries(
			[
				(done) => {
					this.monitorRemoveOrphans(monitors, components, done);
				},
				(done) => {
					this.monitorAddMotherless(monitors, components, done);
				},
				this.monitorAdjustDimensions,
			],
			() => {
				this.doingMonitorAdjustments = false;
			}
		);
	}

	/**
	 * Returns a list of window descriptors that includes each window that the launcher has spawned.
	 */
	getActiveDescriptors(): Record<string, WindowDescriptor> {
		const descriptors: { [k: string]: WindowDescriptor } = {};
		const allActiveWindows = activeWindows.getWindows();
		for (const name in allActiveWindows) {
			descriptors[name] = allActiveWindows[name].windowDescriptor;
		}
		Logger.system.debug(`getActiveDescriptors called: activeWindows: ${Object.keys(allActiveWindows)}`);
		return descriptors;
	}

	/**
	 * Gets all active or pending window names. Active windows have completed spawning.  Pending windows have been spawned but haven't yet completed.
	 * @return {array} list of active or pending window names
	 */
	getActiveAndPendingWindowNames(): string[] {
		const allNames: any[] = [];
		const allActiveWindows = activeWindows.getWindows();

		for (const name in allActiveWindows) {
			allNames.push(name);
		}

		for (var name in this.pendingWindows) {
			allNames.push(name);
		}

		Logger.system.debug("getActiveAndPendingWindowNames", allNames);

		return allNames;
	}

	/**
	 * Get list of windows objects that haven't finished spawning
	 * Individual window objects are not full windows,
	 * they contain the window name and a few other attributes
	 */
	getPendingWindows(): Record<string, PendingWindow> {
		return this.pendingWindows;
	}

	/**
	 * Returns an map of components that can receive specific data types based on "advertiseReceivers" in the component config
	 *
	 * @param {array} dataTypes A list of dataTypes (string)
	 */
	getComponentsThatCanReceiveDataTypes(dataTypes: object) {
		if (!dataTypes || !Array.isArray(dataTypes)) {
			Logger.system.error("Invalid dataTypes passed to getComponentsThatCanReceiveDataTypes");
			return {};
		}
		const componentsThatCanReceiveDataTypes = {};
		for (var c in Components) {
			const component = Components[c];
			let receivers = component.component.advertiseReceivers;
			if (!receivers) {
				continue;
			}
			if (!Array.isArray(receivers)) {
				receivers = [receivers];
			}
			const commonDataTypes = receivers.filter((o) => dataTypes.includes(o));
			for (let i = 0; i < commonDataTypes.length; i++) {
				const commonType = commonDataTypes[i];
				if (!componentsThatCanReceiveDataTypes[commonType]) {
					componentsThatCanReceiveDataTypes[commonType] = {
						componentTypes: [c],
						activeComponents: [],
					};
				} else {
					componentsThatCanReceiveDataTypes[commonType].componentTypes.push(c);
				}
				var allActiveWindows = activeWindows.getWindows();
				const activeComponentNames = Object.keys(allActiveWindows).filter(
					(name) => allActiveWindows[name].componentType === c
				);
				for (const j of activeComponentNames) {
					componentsThatCanReceiveDataTypes[commonType].activeComponents.push(j);
				}
				// componentsThatCanReceiveDataTypes[commonType].activeComponents = [...componentsThatCanReceiveDataTypes[commonType].activeComponents, ...activeComponents];
			}
		}
		for (const dataType of dataTypes) {
			if (!componentsThatCanReceiveDataTypes[dataType]) {
				componentsThatCanReceiveDataTypes[dataType] = {
					componentTypes: [],
					activeComponents: [],
				};
			}
		}
		return componentsThatCanReceiveDataTypes;
	}

	getDefaultConfig(componentType: string): ComponentConfig | null {
		const defaultConfig = Components[componentType];

		return defaultConfig || null;
	}

	/**
	 * Accepts a new list of components. This is typically called as a result of a "dynamic config"
	 * operation (when `ConfigClient.processAndSet()` is called to add or remove apps).
	 *
	 * The new component list is processed and loaded, completely replacing the previous set.
	 *
	 * Note: Use `registerNewComponent()` to add a single component.
	 * @param err
	 * @param componentConfig
	 */
	onComponentListChanged(newComponents: Record<string, ComponentConfig>) {
		Logger.system.debug("Launcher getConfig finsemble.components", newComponents);
		this.loadComponents(newComponents);

		// Send the updated component list to any listeners (e.g. AppLauncher, interop service)
		RouterClient.publish("Launcher.update", {
			componentList: Components,
		});
	}

	/**
	 * Gets the list of components, then listen for changes on the components.
	 */
	async getConfig() {
		Logger.system.debug("LauncherStartup: getConfig");

		ConfigClient.addListener({ field: "finsemble.preloads" }, (err: any, data: any) => {
			if (!err) {
				this.finsembleConfig.preloads = data.value;
			}
		});

		ConfigClient.addListener({ field: "finsemble.extensions" }, (err: any, data: any) => {
			if (!err) {
				this.finsembleConfig.extensions = data.value;
			}
		});

		ConfigClient.addListener({ field: "finsemble.servicesConfig" }, (err: any, data: any) => {
			if (!err) {
				this.finsembleConfig.servicesConfig = data.value;
			}
		});

		const { err, data: manifest } = await ConfigClient.getManifest();
		if (err || !manifest) {
			Logger.system.error("Launcher.getConfig() error:", err);
		} else {
			this.fullManifest = manifest;
			this.finsembleConfig = manifest.finsemble;
			this.persistURL = ConfigUtil.getDefault(
				manifest.finsemble,
				"finsemble.servicesConfig.workspace.persistURL",
				false
			);
			this.persistPath = ConfigUtil.getDefault(
				manifest.finsemble,
				"finsemble.servicesConfig.workspace.persistPath",
				false
			);
		}
	}

	/**
	 *
	 * @param {*} params
	 * @param {string} params.windowIdentifier
	 *
	 */
	getGroupsForWindow(params: { windowIdentifier: WindowIdentifier }): any[] {
		Logger.system.debug("Get Groups Window is in", params.windowIdentifier);
		const groups: any[] = [];
		for (const g of Object.keys(self.windowGroups)) {
			if (self.windowGroups[g].getWindow(params.windowIdentifier)) {
				groups.push(g);
			}
		}
		return groups;
	}

	/**
	 * Gets the proper monitor for a config.
	 * @param {string|number} params.monitor Monitor description, eg, "0" or "primary" or "mine".
	 * @param {*} cb
	 */
	async getMonitorInfo(
		params: {
			monitor?: MonitorCommand;
			windowIdentifier?: WindowIdentifier;
			monitors: MonitorInfoDetail[];
			wiMonitorInfo: MonitorInfoDetail;
		},
		cb: (err: any, monitor: MonitorInfoDetail) => void
	) {
		// default to the monitor of the window that called getMonitorInfo.
		params.monitor = params.monitor || "mine";
		// Collect some asynchronous information we need to make our calculations. First all monitors.
		const addMonitors = async () => {
			const monitors = await getAllMonitors();
			params.monitors = monitors;
		};
		// Next figure out which monitor is associated with the windowIdentifier that was passed in
		const addWIMonitorInfo = () => {
			const dockableMonitor = this.dockingMain.getMonitorForWindow(this);
			params.wiMonitorInfo = dockableMonitor.rawMonitor;
		};

		await addMonitors();
		await addWIMonitorInfo();

		// based on params.monitor and our wiMonitor, figure out which monitor we really want
		const getWhichMonitorParams: GetWhichMonitorParams = {
			commandMonitor: params.monitor,
			monitors: params.monitors,
			launchingMonitorPosition: params.wiMonitorInfo.position ?? 0,
			windowIdentifier: params.windowIdentifier || { windowName: "undefined" },
			activeWindows,
		};
		const myMonitor = await getWhichMonitor(getWhichMonitorParams);
		cb(null, myMonitor);
	}

	/**
	 * Gets all monitors.
	 * @param {*} cb
	 */
	getMonitorInfoAll(
		cb = (err, monitors?: MonitorInfoDetail[]) => {
			err;
			monitors;
		}
	) {
		const self2 = this;
		RouterClient.query("DockingService.getMonitorsFromDocking", {}, (err, message) => {
			if (err) return cb(err);
			const { monitors } = message.data;
			self2.monitors = monitors;
			cb(null, self2.monitors);
		});
	}

	/**
	 * Gets the initial manifest directly from FEA. This manifest does not include all  imported configs.
	 * @param {*} cb
	 */
	async getInitialManifest(): Promise<void> {
		return new Promise<void>((resolve) => {
			const getOptionsSuccess = (opts) => {
				if (opts.customData && opts.customData.manifest) {
					this.initialManifest = opts.customData.manifest;
				} else {
					Logger.system.error("getInitialManifest cannot find manifest in custom data");
				}
				resolve();
			};
			const getOptionsError = (err) => {
				Logger.system.error("getInitialManifest getOptions Error", err);
				resolve();
			};
			System.Window.getCurrent().getOptions(getOptionsSuccess, getOptionsError);
		});
	}

	// get a new name based on sequential counter for base name (repeatable on restart)
	getSequentialName(name: string): string {
		const keyData = localStorage.getItem(NAME_STORAGE_KEY);
		let storageData = {};
		if (keyData) {
			storageData = JSON.parse(keyData);
		}

		// Find next unused sequential name.
		const activeDescriptorNames = Object.keys(this.getActiveDescriptors());
		let newName;
		do {
			if (storageData[name] !== undefined) {
				storageData[name]++;
			} else {
				storageData[name] = 1;
			}
			newName = `${name}-${storageData[name]}`;
		} while (activeDescriptorNames.includes(newName));

		localStorage.setItem(NAME_STORAGE_KEY, JSON.stringify(storageData));

		Logger.system.debug("getSequentialName", name, newName);
		return newName;
	}

	/**
	 * convert a list of window names or identifiers to a list of window objects
	 */
	getWindowsFromNamesOrIdentifiers(windowList, outputAsObject = false): FSBLWindow[] {
		if (!Array.isArray(windowList)) windowList = [windowList];
		let actualWindowList: any;
		if (outputAsObject) {
			actualWindowList = {};
		} else {
			actualWindowList = [];
		}
		for (let i = 0; i < windowList.length; i++) {
			const w = activeWindows.getWindow(windowList[i]);
			if (w) {
				if (outputAsObject) {
					actualWindowList[w.name] = w;
				} else {
					actualWindowList.push(w);
				}
			}
		}
		return actualWindowList;
	}

	/**
	 * When each component finishes shutting down, it reports back to the Launcher via this channel.
	 */
	handleShutdownCompleted(err: string | null, response: ResponderMessage) {
		if (this.shutdownList.waitFor.includes(response.data.name)) {
			this.shutdownList.waitFor.splice(this.shutdownList.waitFor.indexOf(response.data.name), 1);
		}

		const STATUS_MESSAGE = `Component shutdownList completed, waiting on the following components to respond to the launcher:${_.difference(
			this.shutdownList.componentsOpenAtShutdown,
			this.shutdownList.componentsResponded
		).join(",")}. Waiting for the following components to complete shutdownActions${this.shutdownList.waitFor}`;
		Logger.system.debug(STATUS_MESSAGE);
		console.debug(STATUS_MESSAGE);
	}

	/**
	 * After being notified that it needs to shutdown, the component will respond to the launcher. This message will tell the Launcher whether it should wait for the component to do some cleanup methods.
	 */
	handleShutdownResponse(err: string | null, response: ResponderMessage) {
		Logger.system.debug("handleShutdownResponse", response.data.name);
		this.shutdownList.componentsResponded.push(response.data.name);
		if (response.data.waitForMe) {
			Logger.system.debug("handleShutdownResponse push to wait", response.data.name);
			this.shutdownList.waitFor.push(response.data.name);
		}
	}

	/**
	 * Periodically send heartbeat check to all the open windows and log errors or warnings as needed.
	 */
	startHeartbeatMonitor() {
		const internalHeartbeatConfig = this.finsembleConfig.services.windowService.config.heartbeatResponseTimeoutDefaults;
		const config = ConfigUtil.getDefault(
			this.finsembleConfig,
			"finsemble.servicesConfig.window.heartbeatResponseTimeout",
			{}
		);

		RouterClient.addListener("Finsemble.heartbeat", (err: string | null, response: ListenerResponse) => {
			if (!!response?.data?.echo) {
				Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */ "received heartbeat from", response.data.windowName);
			}
			if (response.data.type == "component") {
				const activeWindow = activeWindows.getWindow(response.data.windowName);
				if (activeWindow) {
					activeWindow.lastHeartbeat = Date.now();
				}
			}
		});

		const fitInRange = (x, min = internalHeartbeatConfig.min, max = internalHeartbeatConfig.max) => {
			if (x < min) {
				console.warn(`Heartbeat timeout interval must be above the minimum value ${min}. Using ${min}.`);
				return min;
			}
			if (x > max) {
				console.warn(`Heartbeat timeout interval must be below the maximum value ${min}. Using ${min}.`);
				return max;
			}
			return x;
		};

		const crashed = fitInRange(config.crashed || internalHeartbeatConfig.crashed);
		const possiblyCrashed = fitInRange(config.possiblyCrashed || internalHeartbeatConfig.possiblyCrashed);
		const notResponding = fitInRange(config.notResponding || internalHeartbeatConfig.notResponding);

		const handleTransmit = (name: string, type: string) =>
			RouterClient.transmit(HEARTBEAT_TIMEOUT_CHANNEL, { type, window: name });

		setInterval(() => {
			const date = Date.now();
			for (const name of activeWindows.getWindowNames()) {
				var activeWindow = activeWindows.getWindow(name);
				if (activeWindow) {
					if (!activeWindow.lastHeartbeat) {
						continue;
					}

					/**
					 * None of this should be hard coded. Clients should be able to set what
					 * ever intervals they want.
					 */

					const crashedState = date - activeWindow.lastHeartbeat > crashed && !activeWindow.errorSent;
					const possiblyCrashedState = date - activeWindow.lastHeartbeat > possiblyCrashed && !activeWindow.warningSent;
					const notRespondingState =
						date - activeWindow.lastHeartbeat > notResponding && !activeWindow.notRespondingSent;
					const nowRespondingState =
						date - activeWindow.lastHeartbeat <= notResponding &&
						(activeWindow.errorSent || activeWindow.warningSent || activeWindow.notRespondingSent);

					/**
					 * Windows will appear crashed upon wake from sleep. If we get a signal that a window has crashed,
					 * we wait 50ms for any wake events to fire and check the status again.
					 */
					if (crashedState === true) {
						/**
						 * Notifies that a window has crashed.
						 *
						 * NOTE: It is not clear why crashed notification is delayed by 50ms, but this code
						 *       fixes a timing issue encountered with crashed window notification whereby
						 *       the wrong window might be deemed crashed.
						 *
						 * @param crashedWindow the window which has been determined to be crashed
						 * @returns a Function suitable to be invoked at a later time (with a closure to the crashed window)
						 */
						const notifyCrashed = (crashedWindow: FSBLWindow) => () => {
							if (
								crashedWindow.lastHeartbeat &&
								Date.now() - crashedWindow.lastHeartbeat > crashed &&
								!crashedWindow.errorSent
							) {
								crashedWindow.errorSent = true;
								handleTransmit(name, "crashed");
								Logger.system.error("Heartbeat Status: Crashed Window", name);
							}
						};

						// Invoke the notification function
						setTimeout(notifyCrashed(activeWindow), 50);
					} else if (possiblyCrashedState === true) {
						activeWindow.warningSent = true;
						handleTransmit(name, "possiblyCrashed");
						Logger.system.warn("Heartbeat Status: Possibly Crashed Window", name);
					} else if (notRespondingState === true) {
						activeWindow.notRespondingSent = true;
						handleTransmit(name, "notResponding");
						Logger.system.warn("Heartbeat Status: Unresponsive Window", name);
					} else if (nowRespondingState === true) {
						Logger.system.info("Heartbeat Status: Window has returned to a responsive state", name);
						handleTransmit(name, "nowResponding");

						// set all state variables to false now that the window is responding
						activeWindow.warningSent = false;
						activeWindow.errorSent = false;
						activeWindow.notRespondingSent = false;
					}
				}
			}
		}, 1000);
	}

	// Reset all window heartbeats
	resetHeartbeats() {
		for (const name of activeWindows.getWindowNames()) {
			const activeWindow = activeWindows.getWindow(name);
			activeWindow.lastHeartbeat = Date.now();
		}
	}

	// returns turn if window name is already in use by either active or pending-active window
	isWindowNameAlreadyUsed(windowName: string): boolean {
		const result1 = Boolean(activeWindows.getWindow(windowName));
		const result2 = this.pendingWindows.hasOwnProperty(windowName);
		Logger.system.debug(
			"isWindowNameAlreadyUsed",
			windowName,
			"Result1",
			result1,
			"Result2",
			result2,
			"Pending",
			this.pendingWindows
		);
		return result1 || result2; // if active window or pending window
	}

	/**
	 * Ensures that the url is valid, and converts relative urls when they are spotted.
	 *
	 * @param url - The url to convert
	 * @returns A valid url, or undefined if it cannot be converted or validated
	 */
	normalizedURL = (url: string): string | undefined => {
		// If the url is valid then return it (it was probably an absolute path)
		try {
			const validUrl = new URL(url);
			return validUrl.href;
		} catch (e) {}

		// If not, then it may be relative. Convert it to absolute by prepending the moduleRoot ("/finsemble/" folder).
		try {
			const validUrl = new URL(`${this.finsembleConfig.moduleRoot}/${url}`);
			return validUrl.href;
		} catch (e) {
			Logger.system.error("Launcher.addComponent(): Invalid URL from config", url);
		}
	};

	/**
	 * Adds a component to the component store (window.Components).
	 *
	 * @param componentType - The type of component (This is AppId from appd.json, or the type from any components config or LauncherClient.spawn)
	 * @param config The app's config ("component" config format - not AppD format)
	 */
	addComponent(componentType: string, config: ComponentConfig): void {
		Logger.system.debug("Launcher.addComponent() config.window.url", config);
		const windowConfig = config.window ?? {};
		const component = config.component ?? {};
		const { appConfig } = config;

		/**
		 * The AppLauncher (UI component) constructs its menu using "displayName". This has historically been set
		 * in the component.displayName field. If that field exists then we use it for backward compatibility
		 * but ideally the displayName should come from the AppD title which is intended for user display.
		 */
		const displayName =
			config.component?.displayName ?? appConfig?.title ?? appConfig?.name ?? appConfig?.appId ?? componentType;

		Components[componentType] = {
			...config,
			// Ensure there is always a `window` config, that only contains normalized urls
			window: {
				...windowConfig,
				url: config.window?.url ? this.normalizedURL(config.window.url) : undefined,
			},
			// Ensure there is always a `foreign` config, even if empty
			foreign: config.foreign ?? {},
			// Ensure there is always a `component` config, that at least contains `type`
			component: {
				...component,
				type: componentType,
				displayName,
			},
			icons: getIcons(this.fullManifest, config),
		};
	}

	/**
	 * Resets and recreates the list of component configs.
	 * @param {ComponentMap} newComponents the list of component from the config.
	 * @private
	 */
	loadComponents(newComponents: ComponentMap): void {
		Logger.system.log("LauncherStartup: loadComponents finsemble config", this.finsembleConfig);
		Components = {};

		Object.keys(newComponents).forEach((componentType) => {
			const config = newComponents[componentType];
			// skip components without config (which may exist as `<componentType>:null if they have been removed dynamically`)
			if (!!config) {
				this.addComponent(componentType, config);
			}
		});
	}

	/**
	 *
	 * @param {*} response  - query responder response
	 * @param {String} response.data.componentType - The component name
	 * @param {Object} response.data.manifest - ""
	 */
	registerComponent(err: StandardError, message: ResponderMessage) {
		if (!message.data) {
			return message.sendQueryResponse("no data passed in");
		}
		const params = message.data;
		if (params.manifest && typeof params.manifest !== "object") {
			params.manifest = new LauncherDefaults().componentDescriptor;
		} else {
			// fill in any information that we don't have on the manifest with our defaults.
			// If the user failed to provide a URL, they get an unknown component.
			let defaultConfig: ComponentConfig | null = this.getDefaultConfig(this.getUnknownComponentName());
			if (defaultConfig) {
				let defaultConfigCopy = JSON.parse(JSON.stringify(defaultConfig));
				params.manifest = merge(defaultConfigCopy, params.manifest);
			}
		}
		if (!params.componentType || !params.manifest) {
			// return error
			return message.sendQueryResponse("missing required fields");
		}
		// validate manifest
		if (Components[params.componentType]) {
			Logger.error("Launcher Servers:", params.componentType, "Already registered");
			return message.sendQueryResponse(null, "Component already registered");
		}
		this.addComponent(params.componentType, params.manifest);
		this.update();
		message.sendQueryResponse(null, "success");
	}

	/**
	 *
	 * @param {*} response
	 * @param {String} response.data.componentType - The component name
	 */
	unRegisterComponent(err: StandardError, message: ResponderMessage) {
		if (!message.data) {
			return message.sendQueryResponse("no data passed in");
		}
		const params = message.data;
		if (!params.componentType) {
			// return error
			return message.sendQueryResponse("missing required fields");
		}
		if (Components[params.componentType]) {
			delete Components[params.componentType];
		}
		this.update();
		message.sendQueryResponse(null, "success");
	}

	/**
	 * Force-kill/close all pending windows (i.e. those where spawn hasn't completed).
	 * Used during a workspace switch to stop pending windows (both those spawned by
	 * a user and those from a previous workspace switch) from delaying the switch or
	 * being orphaned due to a timeout.
	 */
	forceKillPendingWindows() {
		for (const windowName in this.pendingWindows) {
			const pendingWindow = this.pendingWindows[windowName];
			//2020-03-09 `pendingWindow.addToWorkspace` will be false for windows spawned by a workspace as they are already in the workspace, hence, kill 'em all!
			Logger.system.log("forceKillPendingWindows removing from pending", windowName, this.pendingWindows[windowName]);

			if (pendingWindow.windowType === "NativeWindow") {
				//We can only force-kill web windows currently
				//Native windows will be removed from pending. When they eventually come up they
				//will appear. If spawned by a workspace they will not be added to the workspace (which may have changed)
				//If spawned by a user/API call with addToWorkspace: true they will be added to the workspace.

				Logger.system.warn(
					`forceKillPendingWindows -- pending native window abandoned. When it loads it will ${
						!pendingWindow.addToWorkspace ? "NOT " : ""
					} be added to the workspace`,
					windowName
				);
			} else {
				if (pendingWindow.windowType !== "WebWindow") {
					Logger.system.error(
						`forceKillPendingWindows -- window type "${pendingWindow.windowType}" not recognized for windowName ${windowName}, will attempt to treat it as a WebWindow`
					);
				}

				try {
					//Use FEA to kill the window as a wrap won't work if the window is still pending
					System.Window.wrap(windowName).close(
						true,
						() => {
							//transmit so we can short circuit any workspace spawns waiting on the window
							RouterClient.transmit(`${windowName}.forceKilled`, {});
							Logger.system.log("forceKillPendingWindows -- web window closed", windowName);
						},
						(reason) => {
							Logger.system.error(
								`forceKillPendingWindows -- window (${windowName}) failed to close. Reason: `,
								reason
							);
						}
					);
				} catch (err) {
					const errorMessage = `forceKillPendingWindows caught error: ${err}`;
					Logger.system.warn(errorMessage);
				}
			}

			//Stop tracking this window whether or not we actually killed it successfully...
			delete this.pendingWindows[windowName];
		}
	}

	/**
	 * Private utility function to sleep/wait for a specified time before accepting the returned promise.
	 * @private
	 */
	private sleep(milliseconds: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	}

	/**
	 * Changes initial window bounds to cached window bounds -- typically cached bounds are the restore location when window is in a maximized state
	 * @param {object} params -- same params passed into finishSpawn.
	 * @param {object} windowDescriptor -- window descriptor passed into spawn.
	 * @private
	 */
	private changeCurrentBoundToRestoreBounds(params: SpawnParams, windowDescriptor: WindowDescriptor) {
		if (params.options?.cachedBounds) {
			// spawn with the original bounds
			windowDescriptor.bounds = params.options.cachedBounds;

			// assimilation is inconsistent, using different bounds for initial window location
			if (windowDescriptor.windowType == "NativeWindow") {
				windowDescriptor.defaultTop = params.options.cachedBounds.top;
				windowDescriptor.defaultBottom = params.options.cachedBounds.bottom;
				windowDescriptor.defaultLeft = params.options.cachedBounds.left;
				windowDescriptor.defaultRight = params.options.cachedBounds.right;
				windowDescriptor.defaultWidth = params.options.cachedBounds.width;
				windowDescriptor.defaultHeight = params.options.cachedBounds.height;
			}
		} else {
			Logger.system.warn("preSpawnHandleMinMaxWindowState: no cached bounds");
		}
	}

	/**
	 * Override window properties to spawn hidden, but track the override so can later return back to original values in restoreOriginalVisibilityValues
	 * @param {object} params -- same params passed into finishSpawn.
	 * @param {object} windowDescriptor -- window descriptor passed into spawn.
	 * @private
	 */
	private overRideVisibilityToSpawnHidden(params: SpawnParams, windowDescriptor: WindowDescriptor) {
		Logger.system.debug("overRideVisibilityToSpawnHidden", params, "windowDescriptor", windowDescriptor);
		this.originalVisibilityValueMap[windowDescriptor.name ?? "undefined"] = {
			opacity: windowDescriptor.opacity,
			autoShow: windowDescriptor.autoShow,
		};
		// native window can only be spawned hidden using autoShow
		if (windowDescriptor.windowType === "NativeWindow") {
			windowDescriptor.autoShow = false;
			// Non-native windows must be spawned hidden using opacity because of Electron 9 problem with rendering if autoShow set to false..
		} else {
			windowDescriptor.opacity = 0;
			// StackedWindows children have autoShow set to false, so need to override that possibility...again because of Electron 9 problem.
			windowDescriptor.autoShow = true;
		}
	}

	/**
	 * If a window's properties have been modified to spawn hidden, restore here back to original value
	 * @param {object} params -- same params passed into finishSpawn.
	 * @param {object} activeWindow -- wrapper of active window
	 * @private
	 */
	private restoreOriginalVisibilityValues(params: SpawnParams, activeWindow: FSBLWindow) {
		Logger.system.debug("restoreOriginalVisibilityValues", params, activeWindow.name, this.originalVisibilityValueMap);
		// need to return autoShow to true; otherwise, remains hidden after restore + reload workspace
		if (this.originalVisibilityValueMap[activeWindow.name]) {
			let originalValues = this.originalVisibilityValueMap[activeWindow.name];
			delete this.originalVisibilityValueMap[activeWindow.name];
			activeWindow._updateOptions({
				opacity: originalValues.opacity,
				autoShow: originalValues.autoShow,
			});
		}
	}

	/**
	 * Adds special pre-spawn behavior to support min/max behavior, based on min/max state in params.options (which comes from what was saved in workspace)
	 * @param {object} params -- same params passed into spawn.
	 * @param {object} windowDescriptor -- window descriptor passed into spawn.
	 * @private
	 */
	private preSpawnHandleMinMaxWindowState(params: SpawnParams, windowDescriptor: WindowDescriptor) {
		Logger.system.debug("preSpawnHandleMinMaxWindowState", params, "windowDescriptor", windowDescriptor);

		params = params || {};
		params.options = params.options || {};
		const { windowState } = params.options;

		// don't initially show windows that will be minimized or maximized in handlePostSpawnBehavior;
		// for maximize state, first spawn the window with its original bounds, then later maximize it -- this keeps internal state consistent
		if (windowState === WINDOWSTATE.MINIMIZED_WAS_NORMAL) {
			this.overRideVisibilityToSpawnHidden(params, windowDescriptor);
		} else if (windowState === WINDOWSTATE.MAXIMIZED || windowState === WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED) {
			this.overRideVisibilityToSpawnHidden(params, windowDescriptor);
			this.changeCurrentBoundToRestoreBounds(params, windowDescriptor);
		}
	}

	/**
	 * Adds the newly spawned window tab collection if specified or creates a new tab collection
	 * with the spawning window
	 *
	 * @param windowName
	 * @param params
	 * @private
	 */
	private async addToTabCollectionOnSpawn(windowName: string, params: AdjustedSpawnParams) {
		if (typeof params.addToTabCollection === "string") {
			return WindowClient.addToTabCollection({
				tabCollectionId: params.addToTabCollection,
				windowNames: [windowName],
				promotedTab: windowName,
			});
		} else if (params.addToTabCollection === true) {
			const windowIdentifier = params.relativeWindow || params.launchingWindow;
			if (windowIdentifier?.windowName) {
				const { err, data } = await WindowClient.getTabCollectionForWindow({ windowName: windowIdentifier.windowName });

				if (err) {
					return await WindowClient.createTabCollection({
						windowNames: [windowIdentifier.windowName, windowName],
						promotedTab: windowName,
					});
				} else if (data?.id) {
					return WindowClient.addToTabCollection({
						tabCollectionId: data.id,
						windowNames: [windowName],
						promotedTab: windowName,
					});
				}
			}
		}

		return false;
	}

	/**
	 * Adds special post-spawn code to handle cases where spawn must have finished on both the client and service side.
	 * Handles case to support min/max behavior, based on min/max state in params.options (which comes from what was saved in workspace)
	 * Handles a request to group on spawn that was initiated from the spawn command.
	 * @param {object} activeWindow -- wrapper of active window
	 * @param {object} params -- same params passed into finishSpawn.
	 * @private
	 */
	private async handlePostSpawnBehavior(activeWindow: FSBLWindow, params: AdjustedSpawnParams) {
		const DELAY_FOR_SYNC_TIME = 2000;
		Logger.system.debug("finishSpawn:handlePostSpawnBehavior", params);

		// Force window to bring to front.
		activeWindow._bringToFront({});

		// Save any user defined title to the workspace (params.tabTitle will be unset by this point if addToWorkspace is not true)
		if (params.tabTitle) {
			this.store.dispatch(
				actions.saveSpawnTabTitleToWorkspace({
					tabTitle: params.tabTitle,
					windowName: activeWindow.windowName,
				})
			);
		}

		if (params.options?.windowState === WINDOWSTATE.MINIMIZED_WAS_NORMAL) {
			await this.sleep(DELAY_FOR_SYNC_TIME);
			// initialLoad parameter signals not to dirty workspace with this operation
			activeWindow._minimize({ initialLoad: true }, () => {
				Logger.system.debug("finishSpawn:handlePostSpawnBehavior:MINIMIZED_WAS_NORMAL done");
				this.restoreOriginalVisibilityValues(params, activeWindow);
			});
		} else if (params.options?.windowState === WINDOWSTATE.MAXIMIZED) {
			// there are multiple synchronization problems if don't pause here before maximizing (for one, window might not yet be registered yet with docking)
			await this.sleep(DELAY_FOR_SYNC_TIME);

			// initialLoad parameter signals not to dirty workspace with this operation
			activeWindow._maximize({ initialLoad: true }, () => {
				Logger.system.debug("finishSpawn:handlePostSpawnBehavior:MAXIMIZED done");
				this.restoreOriginalVisibilityValues(params, activeWindow);
			});
		} else if (params.options?.windowState === WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED) {
			// there are multiple synchronization problems if don't pause here before maximizing (for one, window might not yet be registered yet with docking)
			await this.sleep(DELAY_FOR_SYNC_TIME);
			// initialLoad parameter signals not to dirty workspace with this operation
			activeWindow._maximize({ initialLoad: true }, () => {
				activeWindow._minimize({ initialLoad: true }, () => {
					Logger.system.debug("finishSpawn:handlePostSpawnBehavior:MINIMIZED_WAS_MAXIMIZED done");
					this.restoreOriginalVisibilityValues(params, activeWindow);
				});
			});
		} else if (params.addToTabCollection) {
			await this.addToTabCollectionOnSpawn(activeWindow.windowName, params);
		} else if (params.groupOnSpawn || params.dockOnSpawn) {
			// Group the window on spawn if requested, and the spawn position would be adjacent to the relative window
			// We must wait until the window has finished spawned and registered with docking to group.
			const adjacentSideExists =
				params.left === "adjacent" ||
				params.right === "adjacent" ||
				params.top === "adjacent" ||
				params.bottom === "adjacent";
			if (adjacentSideExists) {
				await this.sleep(DELAY_FOR_SYNC_TIME);
				const windows = [activeWindow.windowName, params.previousWindow.windowName];
				RouterClient.query(
					"DockingService.groupWindows",
					{
						windows,
						isMovable: true,
					},
					() => {}
				);
			} else {
				Logger.system.warn("Unable to group windows on spawn - at least one side must be adjacent");
			}
		} else if (params.isInExistingGroup) {
			// If the window being spawned is in a group saved in the workspace, make sure the group doesn't spawn off monitor and stays intact relative to itself
			// The window needs have registered with docking and already been grouped before doing this check.
			// Don't block the main process with "await" to finish docking registration before do the check.
			this.sleep(DELAY_FOR_SYNC_TIME).then(() => {
				RouterClient.transmit("DockingService.checkAndFixGroups", { name: activeWindow.name });
			});
		}

		// The _updateOptions() will invoke the Assimilation.saveWindow function to update the default native window options after spawn.
		// If this is not done then there is the possibility of a dirty workspace space issue.
		// For example, open a native window and save the workspace, use a show() function to display the window. After that, the workspace will be dirty.
		if (activeWindow.windowType === "NativeWindow") {
			activeWindow._updateOptions({});
		}
	}

	/**
	 *
	 * @private
	 */
	private async finishSpawn(
		defaultComponentConfig: ComponentConfig,
		windowDescriptor: WindowDescriptor,
		params: AdjustedSpawnParams,
		newWindowIdentifier: WindowIdentifier
	) {
		Logger.console.log("finishSpawn:start", defaultComponentConfig, windowDescriptor, params, newWindowIdentifier);
		Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */ "finishSpawn:start", windowDescriptor.name || "undefined");

		const componentType = defaultComponentConfig?.component?.type;
		if (params.slave) {
			const slaveIdentifier: WindowIdentifier = {
				windowName: windowDescriptor.name ?? "undefined",
				uuid: windowDescriptor.uuid,
			};
			util.getFinWindow(slaveIdentifier, (finWindow) => {
				if (!finWindow) return;
				self.makeSlave(finWindow, {
					windowName: params.previousWindow.name,
					uuid: params.previousWindow.uuid,
				});
			});
		}

		windowDescriptor.uuid = windowDescriptor.uuid || uuidv4();

		const result = {
			windowIdentifier: {
				windowName: windowDescriptor.name,
				uuid: windowDescriptor.uuid,
				componentType,
				monitor: windowDescriptor.monitorInfo,
				windowType: windowDescriptor.windowType,
			},
			windowDescriptor,
		};

		// Deprecated value: this.windowOptions.customData.component.canMinimize. New value: this.windowOptions.customData.foreign.services.windowService.allowMinimize
		const service =
			windowDescriptor?.customData?.foreign.services &&
			windowDescriptor.customData.foreign.services.windowService !== undefined
				? "windowService"
				: "dockingService";

		// Look first in the new location.
		let canMinimize = windowDescriptor?.customData?.foreign?.services[service]?.allowMinimize;
		let canMaximize = windowDescriptor?.customData?.foreign?.services[service]?.allowMaximize;

		// If the new location isn't found, fall back to deprecated version
		if (canMinimize === undefined) {
			({ canMinimize } = windowDescriptor?.customData?.component);
		}
		if (canMaximize === undefined) {
			({ canMaximize } = windowDescriptor?.customData?.component);
		}

		// Store references to the actual window we've created. Clients can use LauncherClient.getRawWindow()
		// to get direct references for (god forbid) direct DOM manipulation
		let activeWindowParams = {
			name: windowDescriptor.name,
			uuid: windowDescriptor.uuid,
			// If they left canMinimize un-configured, coerce undefined to be true, which is the default
			canMinimize: canMinimize !== false,
			canMaximize: canMaximize !== false,
			windowIdentifier: result.windowIdentifier,
			windowDescriptor,
			params,
			windowType: windowDescriptor.windowType,
		};

		if (windowDescriptor.windowType === "FinsembleNativeWindow") {
			// Since objectReceivedOnSpawn is the only thing that doSpawn gets to send to finishSpawn, this is where everything that the wrap needs resides
			// Note: objectReceivedOnSpawn used to be 'finWindow
			activeWindowParams = merge(newWindowIdentifier, activeWindowParams);
		}

		Logger.system.debug(":wrap: finishSpawn", activeWindowParams);
		const { wrap: activeWindow } = await FinsembleWindowInternal.getInstance(activeWindowParams);
		activeWindow.wrapReady();
		activeWindow.addEventListener("closed", self.remove);
		activeWindow.windowDescriptor = windowDescriptor; // background note: the windowDescriptor was on the public instance, but not the private, so adding it here.  Required elsewhere (e.g. getActiveDescriptors)
		activeWindows.addWindow(activeWindow);
		Logger.system.log(`finishSpawn: added window:${activeWindow.name}`);

		delete this.pendingWindows[windowDescriptor.name ?? "undefined"]; // active now so no long pending...can remove

		// Add window to a group if needed
		if (params.groupName) {
			if (!this.windowGroups[params.groupName]) {
				this.createWindowGroup(null, {
					data: {
						groupName: params.groupName,
						windowList: [result.windowIdentifier],
					},
				});
			} else {
				const actualWindow = this.getWindowsFromNamesOrIdentifiers([result.windowIdentifier]);
				this.windowGroups[params.groupName].addWindows(actualWindow);
				this.sendUpdatesToWindows(actualWindow);
			}
		}

		//shows devTool for components with config parameter "showDevConsoleOnVisible : true"
		if (windowDescriptor?.customData?.component.showDevConsoleOnVisible === true) {
			System.showDeveloperTools(activeWindow.name, null);
		}

		Logger.perf.debug("Spawn", "stop", componentType, "from finishSpawn");

		await this.handlePostSpawnBehavior(activeWindow, params);

		return Promise.resolve({ err: null, data: result });
	}

	/**
	 * Makes a slave window which will automatically close when the master closes.
	 * @param  {finWindow} slave  An Electron window
	 * @param  {LauncherClient~windowIdentifier} master The window identifier of the master
	 */
	makeSlave(slave: FEAWindow, master: WindowIdentifier) {
		util.getFinWindow(master, (masterWindow) => {
			if (masterWindow) {
				const closedHandler = () => {
					Logger.system.debug("makeSlave close", slave.name);
					slave.close();
					masterWindow.removeEventListener("closed", closedHandler);
				};
				masterWindow.addEventListener("closed", closedHandler);
			}
		});
	}

	/**
	 * See doMonitorAdjustments()
	 * Adds any motherless components. These would be any spawnOnAllMonitor components that are missing from a
	 * particular monitor, presumably because the monitor just got added.
	 */
	monitorAddMotherless(monitors: MonitorInfoDetail[], components: Record<string, ComponentConfig>, done) {
		let howMany = 0;
		for (let i = 0; i < monitors.length; i++) {
			for (const c in components) {
				const component = components[c];
				howMany++;
				if (!component[i]) {
					self.spawn({ component: c, monitor: i }, (err, result) => {
						result.windowDescriptor.spawnOnAllMonitors = true; // WHY WHY WHY DO WE NOT DO THIS BEFORE HAND
						howMany--;
						if (!howMany) {
							done();
						}
					});
				} else {
					setTimeout(() => {
						// prevent multiple events and showWindow stuff is handled by adjustMonitorDimensions
						howMany--;
						if (!howMany) {
							done();
						}
					}, 50);
				}
			}
		}
		if (!howMany) {
			done();
		}
	}

	/**
	 * See doMonitorAdjustments()
	 * Adjust component dimensions. Since the monitor size may have changed we need to adjust any components
	 * that had previously made assumptions about monitor size (such as a toolbar that is supposed to stretch across
	 * the top of the screen). We cycle through any components that have made a "claim" on monitor space and then
	 * simply call showWindow() with their original params in order to give them a chance to resettle.
	 */
	monitorAdjustDimensions(done: () => void) {
		Logger.system.debug("monitorAdjustDimensions");
		const claims = {};
		const allActiveWindows = activeWindows.getWindows();
		for (const windowName in allActiveWindows) {
			const entry = allActiveWindows[windowName];
			const w = entry.windowDescriptor;
			if (!w) continue;
			// Create a stash of all claims, and then unclaim them to set our
			// algorithm back to square
			if (w.claimMonitorSpace) {
				claims[windowName] = entry;
				delete w.claimMonitorSpace;
			}
		}

		// Now we simply call showWindow for each item in our stash with the original
		// params. This will reset it back, and should cause it to adjust accordingly
		// to the monitor it is now sitting on.
		for (const claimedName in claims) {
			const entry = claims[claimedName];
			self.showWindow(entry.windowIdentifier, entry.params, () => {
				entry.windowDescriptor.claimMonitorSpace = true;
			});
		}

		done();
	}

	/**
	 * see doMonitorAdjustments()
	 * Removes any orphaned components. These would be any spawnOnAllMonitor components that are now located
	 * on a different monitor than they started. We simply compare their existing monitor with the one they were
	 * spawned upon, and remove them if they aren't where they belong.
	 */
	monitorRemoveOrphans(monitors: MonitorInfoDetail[], components: Record<string, ComponentConfig>, done) {
		// All this stuff only works for OF Windows
		const allActiveWindows = activeWindows.getWindows();
		let howMany = 0;
		for (const w in allActiveWindows) {
			const win = allActiveWindows[w];
			const { windowDescriptor } = win;
			if (!windowDescriptor) continue;
			if (windowDescriptor.customData?.component.spawnOnAllMonitors) {
				const componentType = windowDescriptor.customData.component.type;
				const componentMonitor = windowDescriptor.monitorInfo;
				if (!monitors[componentMonitor]) {
					// remove window if no monitor - do we want to remove the window? might be better for performance if we let it stick around.
					howMany++;
					win.close(null, () => {
						howMany--;
						if (!howMany) {
							done();
						}
					});
				} else if (components[componentType]) {
					components[componentType][windowDescriptor.monitorInfo] = win;
				} else {
					Logger.system.error(`monitorRemoveOrphans: component type ${componentType} note found`);
				}
			}
		}
		if (!howMany) {
			done();
		}
	}

	/**
	 * Removes a component. This is called when a window receives a closed event.
	 * If the window is still open then it is closed.
	 *
	 * @param  {string}   windowName Name of window that was closed
	 */
	remove(event: FinsembleEvent) {
		let windowName;
		if (event.source) {
			// The stack close triggers a wrapper event.
			windowName = event.source;
		} else {
			windowName = event;
		}

		Logger.system.info("Launcher.remove: windowName", windowName);

		if (!windowName) return;
		// this block is for legacy support. all calls to remove in the Launcher pass in the windowDescriptor. The old functionality was to pass in a window name.
		if (typeof windowName !== "string") {
			const descriptor = JSON.parse(JSON.stringify(windowName));
			windowName = descriptor.name;
		}
		const activeWindow = activeWindows.getWindow(windowName);

		if (!activeWindow) {
			Logger.system.debug("Launcher.remove: Active Window not found", windowName);
			return;
		}
		// This is the only place in the application where we remove close listeners.
		activeWindow.removeEventListener("closed", self.remove);
		Logger.system.debug("Launcher.remove: this.remove", activeWindow);

		if (activeWindow.windowDescriptor && activeWindow.windowDescriptor.claimMonitorSpace) {
			// stacked windows will have these properties
			self.getMonitorInfoAll((monitors) => {
				RouterClient.publish("monitorInfo", monitors);
			});
		}

		// remove the window from all groups
		if (self.windowGroups) {
			for (const g of Object.keys(self.windowGroups)) {
				const group = self.windowGroups[g];
				group.removeWindows([windowName]);
				// if group is empty delete it
				if (!Object.keys(group.getWindows()).length) {
					group.destroy();
					delete self.windowGroups[g];
				}
			}
		}
		activeWindows.removeWindows([windowName], () => {
			Logger.system.debug("launcher.remove wrappers for window", windowName);
			// MyWrapManager.remove({ identifier: { windowName: windowName } }, () => {
			// This is to prevent workspaces/stackManager from catching this event and removing the window. Might be better for the launcher to send out a note to everyone who cares "Hey guys, I'm shutting down, go ahead and remove any listeners that you don't want to accidentally fire when these windows start closing". This is the expedient fix. That would probably be better, but require more architectural changes and testing.
			if (self.shuttingDown) {
				Logger.system.log(
					"Component removed.",
					windowName,
					"Not transmitting the windowClosed event because the application is shutting down"
				);
				return;
			}
			// Given the context above, we use the router to transmit out an event instead of relying on window events.
			// The WorkspaceService used to listen for this message, but is now handled in the windowPrimitiveEntryPoint.closeHandler
			// The InteropService, RemoteRedux and PrivateEventManager all listen for this transmission to know when windows close
			RouterClient.transmit(LAUNCHER_SERVICE.WINDOW_CLOSED, {
				uuid: activeWindow.uuid,
				name: windowName,
			});
		});
	}

	// Removes user defined component from component map
	removeUserDefinedComponent(message: RouterMessage, cb: StandardCallback) {
		let err: string | null = null;
		if (Components[message.data.name]) {
			delete Components[message.data.name];
		} else {
			err = `Could not find component of type ${message.data.name}`;
		}
		this.update();
		cb(err, null);
	}

	/**
	 * Will reset the spawn stagger.
	 * @param {object} [params]
	 * @param {number} [params.monitorPosition] position of monitor to reset the stagger for
	 */
	resetSpawnStagger(params: { monitorPosition: number }) {
		const EMPTY_STAGGER = { x: null, y: null };

		if (params && typeof params.monitorPosition !== "undefined") {
			this.lastOpenedMap[params.monitorPosition] = EMPTY_STAGGER;
			this.lastAdjustedMap[params.monitorPosition] = EMPTY_STAGGER;
		} else {
			for (const monitorPosition in this.lastOpenedMap) {
				this.lastOpenedMap[monitorPosition] = EMPTY_STAGGER;
				this.lastAdjustedMap[monitorPosition] = EMPTY_STAGGER;
			}
		}
	}

	/**
	 * Whenever windows are added/removed from groups, send updates to existing windows with their group memberships.
	 * @param {} windowList
	 */
	sendUpdatesToWindows(windowList: any) {
		Logger.system.debug("List of Updated Windows", windowList);
		for (const w in windowList) {
			const win = windowList[w];
			let groups = self.getGroupsForWindow({
				// @ts-ignore
				windowIdentifier: win.windowIdentifier,
			});
			if (!Array.isArray(groups)) groups = [];
			if (win) RouterClient.publish(`Finsemble.LauncherService.updateGroups.${win.name}`, groups);
		}
	}

	// Returns active window to be shown.
	private getActiveWindow(windowIdentifier: WindowIdentifier): FSBLWindow | null {
		let activeWindow: FSBLWindow | null = null;
		if (windowIdentifier.windowName) {
			activeWindow = activeWindows.getWindow(windowIdentifier.windowName);
		} else if (windowIdentifier.componentType) {
			activeWindow = self.componentFinder(windowIdentifier);
		}
		return activeWindow;
	}

	// Finds a specific monitor for showing active window
	private async specificMonitorFinder(params) {
		const monitors = await getAllMonitors();
		for (let i = 0; i < monitors.length; i++) {
			if (
				monitors[i].position === params.monitor ||
				monitors[i].name === params.monitor ||
				monitors[i].whichMonitor === params.monitor
			) {
				return monitors[i];
			}
		}
		return null;
	}

	// By default, return the first monitor.
	private async monitorFinder() {
		const monitors = await getAllMonitors();
		return monitors[0];
	}

	// Returns promise to find relative monitor for showing active window
	private relativeMonitorFinder(params): Promise<MonitorInfoDetail | null> {
		return new Promise<MonitorInfoDetail>(async (resolve) => {
			const relativeWindow = params.relativeWindow ? activeWindows.getWindow(params.relativeWindow.windowName) : null;
			if (!relativeWindow) {
				const monitor = await this.monitorFinder();
				return resolve(monitor);
			}
			relativeWindow._getBounds({}, async (err, relativeBounds) => {
				if (!err) {
					// If a relativeWindow is supplied, the bounds of said window will be fetched more than once
					// in the 'showWindow' process, to speed things up, since the window shouldn't be moving during
					// this process, cache the bounds when retrieved the first time and if they are cached later,
					// skip re-fetching
					params.relativeWindowCachedBounds = relativeBounds;
					// Once we get the monitor, overwrite 'mine' with its position to avoid further calls
					const monitor = await getMonitorFromScaledXY(relativeBounds.left, relativeBounds.top);
					if (monitor && monitor.position) params.monitor = monitor.position;
					resolve(monitor);
				} else {
					const monitors = await getAllMonitors();
					resolve(monitors[0]);
				}
			});
		});
	}

	// Return new bounds for showing an active window
	private getNewBounds(params, newWindowDescriptor: WindowDescriptor): Bounds {
		// Evaluates a given params property. Returns true if the window to show is ephemeral/the given parameter is a string, or the value is missing, otherwise returns false
		const paramMissingOrString = (prop) => {
			const param = newWindowDescriptor[prop];
			return params.ephemeral || typeof param !== "number" || typeof param === "string";
		};

		const { defaultLeft = 0, defaultWidth = 640, defaultTop = 0, defaultHeight = 480 } = newWindowDescriptor;

		// If the window is not ephemeral or the bounds contained in params aren't strings, use the passed values. Otherwise use the values calculated from 'deriveBounds'
		return {
			left: paramMissingOrString("left") ? defaultLeft : (newWindowDescriptor.left ?? 0),
			top: paramMissingOrString("top") ? defaultTop : (newWindowDescriptor.top ?? 0),
			right: paramMissingOrString("right") ? defaultLeft + defaultWidth : (newWindowDescriptor.right ?? 0),
			bottom: paramMissingOrString("bottom") ? defaultTop + defaultHeight : (newWindowDescriptor.bottom ?? 0),
			width: paramMissingOrString("width") ? defaultWidth : (newWindowDescriptor.width ?? 0),
			height: paramMissingOrString("height") ? defaultHeight : (newWindowDescriptor.height ?? 0),
		};
	}

	// show an active window, possibly with new position
	private async showIt(
		params,
		newBounds: Bounds,
		oldBounds: Bounds,
		activeWindow: FSBLWindow,
		windowIdentifier: WindowIdentifier,
		newWindowDescriptor: WindowDescriptor,
		cb
	) {
		const result = {
			windowIdentifier: {
				windowName: activeWindow.name,
				uuid: activeWindow.uuid,
				componentType: windowIdentifier.componentType,
				monitor: newWindowDescriptor.monitorInfo,
			},
			windowDescriptor: newWindowDescriptor,
		};

		if (params.setBoundsOnly) {
			if (cb) {
				cb(null, result);
			}
			return;
		}

		const newBoundsMonitor = await getMonitorFromScaledXY(newBounds.left, newBounds.top);
		const oldBoundsMonitor = await getMonitorFromScaledXY(oldBounds.left, oldBounds.top);

		activeWindow._show(
			{
				switchedMonitors: oldBoundsMonitor.position !== newBoundsMonitor.position,
			},
			() => {
				Logger.system.info("Launcher.ShowWindow.showAt finished", activeWindow.name);
				/*
				 * 7/8/19 Joe - Previously this was assigning newWindowDescriptor defaults.
				 * With all of the logic above to ensure params has every type of bounds,
				 * it seems params should be checked first and only fall back to defaults
				 * if any are missing (which doesn't seem like it would ever be the case)
				 */
				const dockingDescriptor = {
					...newBounds,
					name: activeWindow.name,
					changeType: 1,
				};

				// If the show call doesn't prohibit autofocus, focus it. Search does this when showing search results.
				// This is so that any click elsewhere will hide the window.
				if (params.autoFocus !== false && "focus" in activeWindow) {
					activeWindow.focus();
				}

				// promote window if in a tab collection
				promoteTab(activeWindow.name);

				RouterClient.transmit("DockingService.updateWindowLocation", {
					windowName: windowIdentifier.windowName,
					location: dockingDescriptor,
				});
				if (cb) {
					cb(null, result);
				}
			}
		);
	}

	// Returns params, adjusted for non-absolute positions, for showing an active window
	private async getShowParamPositionInfo(params, theMonitorFinder) {
		const hasLeft = params.left || params.left === 0;
		const hasTop = params.top || params.top === 0;
		const hasRight = params.right || params.right === 0;
		const hasBottom = params.bottom || params.bottom === 0;
		const hasWidth = typeof params.width !== "undefined" && params.width !== 0;
		const hasHeight = typeof params.height !== "undefined" && params.height !== 0;

		const hasAnyPosition = hasLeft || hasTop || hasRight || hasBottom;

		const monitor = await theMonitorFinder(params);

		if (!monitor) {
			Logger.system.error("No monitor found for params", params);
		}
		// Adjust parameters to what deriveBounds expects
		// default to the monitor that the window already lives on
		params.monitor ??= monitor.position;

		// imesner: 20221005: calling spawn on a singleton was calling showWindow without bounds or position,
		// defaulting position to available, and moving the window down when the toolbar was docked.
		// Default to absolute positioning if no bounds are passed to ensure window doesn't move
		if (!hasAnyPosition) {
			params.position = "absolute";
		} else if (!_isOfPositionTypes(params.position)) {
			params.position = "available";
		}

		// The viewport adjusts bounds, so assign monitorRect for monitor positioning,
		// 0s for absolute, and availableRect for all others.  If a monitor wasn't found, just default to 0s.
		let viewport;
		if (params.position === "monitor") {
			viewport = monitor?.monitorRect;
		} else if (params.position !== "absolute") {
			viewport = monitor?.availableRect;
		}

		viewport ??= {
			left: 0,
			right: 0,
			top: 0,
			bottom: 0,
		};

		// Note that these will be NaN if params.right or params.right are "center", "left", etc.
		const deltaRight = hasRight ? viewport.right - params.right : 0;
		const deltaBottom = hasBottom ? viewport.bottom - params.bottom : 0;

		return { hasWidth, hasHeight, hasLeft, hasTop, hasRight, hasBottom, viewport, deltaRight, deltaBottom };
	}

	// returns params, adjusted for non-relative positions, for showing an active window
	private getNonRelativeParams(
		params,
		viewport,
		hasLeft,
		hasTop,
		hasBottom,
		hasRight,
		deltaRight,
		deltaBottom,
		bounds
	) {
		// absolute offset of the left/top side, these values should always be 0 if position is absolute
		let offsetLeft;
		let offsetTop;

		// If right but no left, calculate the left position of the window relative to the viewport.
		if (params.position !== "relative" && !hasLeft && hasRight) {
			offsetLeft = params.width + viewport.left;
			params.left = deltaRight - offsetLeft;
			hasLeft = true;
		}

		// If bottom but no top
		if (params.position !== "relative" && !hasTop && hasBottom) {
			offsetTop = params.height + viewport.top;
			params.top = deltaBottom - offsetTop;
			hasTop = true;
		}

		const absoluteOrNotAdjacentLeftRight =
			params.position === "absolute" || (params.left !== "adjacent" && params.right !== "adjacent");

		// If neither left nor right are set then maintain it's left position
		if (!hasLeft && !hasRight && absoluteOrNotAdjacentLeftRight) {
			params.left = bounds.left;
		}

		const absoluteOrNotAdjacentTopBottom =
			params.position === "absolute" || (params.top !== "adjacent" && params.bottom !== "adjacent");

		// If neither top nor right are set then maintain it's top position
		if (!hasTop && !hasBottom && absoluteOrNotAdjacentTopBottom) {
			params.top = bounds.top;
		}

		return params;
	}

	// get params to show an active window
	private async getShowParams(params, theMonitorFinder, absoluteMonitorFinder, bounds) {
		// A developer can call showWindow with a combination of left, right or width (top, bottom or height).
		// This essentially means that a developer can be setting both position and dimension, just position, or just dimension.
		// Furthermore, they might set one or other position (left or right, top or bottom). The following logic is meant
		// to maintain the dimension or position where not overridden by the developer. This is done by rationalizing
		// the window location to just top,left,width,height.
		let hasLeft;
		let hasTop;
		let hasRight;
		let hasBottom;
		let deltaRight;
		let deltaBottom;
		let viewport;

		let hasWidth = typeof params.width !== "undefined" && params.width !== 0;
		let hasHeight = typeof params.height !== "undefined" && params.height !== 0;

		({ hasWidth, hasHeight, hasLeft, hasTop, hasRight, hasBottom, viewport, deltaRight, deltaBottom } =
			await this.getShowParamPositionInfo(params, theMonitorFinder));

		// absolute distance between the monitor sides and the window sides
		let deltaLeft = hasLeft ? params.left - viewport.left : 0;
		let deltaTop = hasTop ? params.top - viewport.top : 0;

		if (hasLeft && hasRight) {
			params.width = deltaRight - deltaLeft;
		} else if (!hasWidth) {
			params.width = bounds.width;
		}

		if (hasTop && hasBottom) {
			params.height = deltaBottom - deltaTop;
		} else if (!hasHeight) {
			params.height = bounds.height;
		}

		params = this.getNonRelativeParams(
			params,
			viewport,
			hasLeft,
			hasTop,
			hasBottom,
			hasRight,
			deltaRight,
			deltaBottom,
			bounds
		);

		// if no position type is provided we want it to be positioned within the unclaimed rect
		if (!params.position) params.position = "available";

		//For absolute position we need to wait until we've derived a top and left position before getting the monitor
		if (params.position === "absolute") {
			await absoluteMonitorFinder(params.left, params.top);
		}

		return params;
	}

	// Shows active window, considering monitors and relocating it as needed
	private async showActiveWindow(
		activeWindow,
		windowIdentifier: WindowIdentifier,
		params: ShowWindowParams,
		cb: StandardCallback
	) {
		// Remove the window from docking group
		RouterClient.query("DockingService.leaveGroup", { name: windowIdentifier.windowName }, () => {});

		let { data: bounds } = await activeWindow._getBounds();
		// The next 3 lines are needed because the windowIdentifier coming in from the client API is not guaranteed to have all of the information that we need in order to identify the window.
		// All that's needed to retrieve a window is a name. We need to know the componentType to derive default configs for this component.
		windowIdentifier = activeWindow.windowIdentifier ? activeWindow.windowIdentifier : windowIdentifier;
		windowIdentifier.componentType = activeWindow.componentType;
		params.windowIdentifier = windowIdentifier;

		//Return the monitor based on specific bounds
		const absoluteMonitorFinder = async (left, top) => {
			const monitor = await getMonitorFromScaledXY(left, top);
			// 0 is a valid value for monitor.position, so this check needs to be specific to avoid a falsey error.
			if (monitor && monitor.position !== undefined) params.monitor = monitor.position;
			return monitor;
		};
		//don't overwrite the basic monitor finder as we may need it later
		let theMonitorFinder: (params) => Promise<MonitorInfoDetail | null> = this.monitorFinder;

		// When asking for relative positioning, we're implicitly wanting to use the same monitor as the relativeWindow
		if (!params.monitor && params.position === "relative") {
			theMonitorFinder = this.relativeMonitorFinder;
		}
		// If params.monitor is specified, try to get and use that monitor.
		if (typeof params.monitor !== "undefined") {
			if (params.monitor === "primary" || Number.isInteger(params.monitor)) {
				// asked to spawn on specific monitor
				theMonitorFinder = this.specificMonitorFinder;
			} else if (params.monitor === "mine") {
				// asked to spawn on same monitor as parent
				theMonitorFinder = this.relativeMonitorFinder;
			}
		} else {
			params.monitor = "mine";
		}

		params = await this.getShowParams(params, theMonitorFinder.bind(this), absoluteMonitorFinder, bounds);

		// Since we've already calculated the absolute position, we need to make sure deriveBounds respects those coordinates
		if (!["relative", "available", "absolute"].includes(params.position)) params.position = "monitor";
		// params.bottom = null;
		// params.right = null;

		let { windowDescriptor: newWindowDescriptor } = await self.deriveBounds(params);
		const newBounds = this.getNewBounds(params, newWindowDescriptor);

		activeWindow._setBounds({ bounds: newBounds }, () => {
			this.showIt(params, newBounds, bounds, activeWindow, windowIdentifier, newWindowDescriptor, cb);
		});
	}

	/**
	 * ShowWindowAtMouse relocates a specified window to the current mouse position
	 * Citrix windows aren't controlled by finsemble, so the dotnet linker button uses this
	 * private / internal router endpoint to grab the finsemble desktop's mouse
	 * position and use position: absolute with it.  Only tested with the linker.
	 * @param  {LauncherClient~windowIdentifier} windowIdentifier The window to show/move
	 * @param function cb Callback
	 * @private
	 */
	async showWindowAtMouse(windowIdentifier: WindowIdentifier, cb: StandardCallback) {
		const position = await System.getMousePosition();
		const showWindowParams: ShowWindowParams = {
			position: "absolute",
			left: position.x,
			top: position.y,
			// launchingWindow and relativeWindow are used to identify the monitor for the window.
			// A null ref occurs if this isn't here.
			launchingWindow: windowIdentifier,
		};

		await self.showWindow(windowIdentifier, showWindowParams, cb);
	}

	/**
	 * Shows and/or relocates a component window
	 * @param  {LauncherClient~windowIdentifier} windowIdentifier The window to show/move
	 * @param	object params	Parameters, see spawn()
	 * @param function cb Callback
	 */
	async showWindow(windowIdentifier: WindowIdentifier, params: ShowWindowParams, cb: StandardCallback) {
		// Maybe replace the component name if it's a legacy UI component. See comment in ../../../../deprecated/legacyUIComponentNames.ts
		if (windowIdentifier.componentType)
			windowIdentifier.componentType =
				legacyUINameMap[windowIdentifier.componentType] ?? windowIdentifier.componentType;

		Logger.system.info("Launcher.ShowWindow.showAt Start", windowIdentifier, params);
		// do we have a windowName?
		const activeWindow = this.getActiveWindow(windowIdentifier);

		if (activeWindow) {
			this.showActiveWindow(activeWindow, windowIdentifier, params, cb);
		} else {
			// window not found
			if (params.spawnIfNotFound && windowIdentifier.componentType) {
				if (windowIdentifier.windowName) {
					params.name = windowIdentifier.windowName;
				}
				params.component = windowIdentifier.componentType;
				Logger.system.debug("Launcher.ShowWindow.show spawn", windowIdentifier);
				await self.spawn(<SpawnParams>params, (err, data) => {
					Logger.system.debug("Launcher.ShowWindow.show spawn complete", windowIdentifier);
					cb(err, data);
				});
			} else {
				cb("RouterService:showWindow. Requested window not found.");
			}
		}
	}

	/**
	 * Rewrite of shutdownComponents to call close on components.
	 * @param {function} done
	 */
	shutdownComponents(done: () => void) {
		// assume done if everybody doesn't close within alloted time so dependencies can close and shutdownList can continue.
		let shutdownTimeout = this.finsembleConfig.shutdownTimeout ? this.finsembleConfig.shutdownTimeout - 2000 : 8000;
		shutdownTimeout = Math.max(shutdownTimeout, 0); // prevent this from going negative if someone sets a very low shutdownTimeout.
		const myTimeout = setTimeout(() => {
			done();
		}, shutdownTimeout);

		each(
			activeWindows.windows,
			(win, callback) => {
				if (win.windowDescriptor.componentType !== "Freestanding") {
					(win as BaseWindow).close({ removeFromWorkspace: false, ignoreParent: true }, callback);
				} else {
					callback();
				}
			},
			() => {
				// make sure done doesn't get called back twice
				clearTimeout(myTimeout);
				done();
			}
		);
	}

	/**
	 * Removes disallowed parameters before we pass data into compileWindowDescriptor.
	 * @param params SpawnParams
	 */
	_removeDisallowedSpawnParams(params: SpawnParams) {
		// Delete securityPolicy and permissions properties
		// User may try to pass them to override our security settings
		if (params.options) {
			delete params.options.securityPolicy;
			delete params.options.permissions;
		}

		// params is of type spawnParams. SecurityPolicy is not a supported parameter, so we don't want to put it into the type definition and have it documented.
		// the (as any) cast is here so that typescript can build.
		delete (params as any).securityPolicy;
		delete (params as any).permissions;
		return params;
	}

	// Returns array of preload scripts for spawning new component
	private getPreloadScripts(config, preloadScripts, FSBLHeader, disableBrowserView) {
		// @deprecated - servicesConfig.launcher.defaultPreloads is deprecated in 6.6. Remove in 8.0.
		let defaultPreloads: any[] = [];
		// Handle defaultPreloads logged in the servicesConfig
		const globalPreloads = get(this.finsembleConfig, "servicesConfig.launcher.defaultPreloads", {});

		for (const globalPreloadType of Object.keys(globalPreloads)) {
			const globalPreloadList = Array.isArray(globalPreloads[globalPreloadType])
				? globalPreloads[globalPreloadType]
				: [globalPreloads[globalPreloadType]];

			switch (globalPreloadType) {
				case "allComponents":
					defaultPreloads.push(...globalPreloadList);
					break;
				case "launchableComponents":
					if (get(config, "foreign.components.App Launcher.launchableByUser", false)) {
						defaultPreloads.push(...globalPreloadList);
					}
					break;
				case "browserViews":
					if (!disableBrowserView && FSBLHeader) {
						defaultPreloads.push(...globalPreloadList);
					}
					break;
				default:
					break;
			}
		}

		// Handle autoload config from finsemble.extensions.preload
		const extensionPreloads = get(this.finsembleConfig, "extensions.preloads", {});
		for (const preloadAlias in extensionPreloads) {
			if (!extensionPreloads[preloadAlias].autoload) {
				continue;
			}

			const { autoload } = extensionPreloads[preloadAlias];
			if (
				autoload === "all" ||
				(autoload === "launchable" && get(config, "foreign.components.App Launcher.launchableByUser", false))
			) {
				defaultPreloads.push(preloadAlias);
			}
		}

		let preload = Array.isArray(config?.component?.preload) ? config?.component?.preload : [config?.component?.preload];
		let preloadList = (preload ?? []).map((x) => {
			return { url: maybeResolvePreloadAllias(<string>x, this.finsembleConfig) };
		});
		defaultPreloads = defaultPreloads.map((x) => {
			return { url: maybeResolvePreloadAllias(x, this.finsembleConfig) };
		});

		return Array.from(new Set([...preloadList, ...preloadScripts, ...defaultPreloads]));
	}

	// cleanup pendingWindows as needed for failed windows
	private cleanupPendingWindowsMap(baseDescriptorName) {
		let retryAttempt = 0;
		const interval = setInterval(() => {
			retryAttempt++;
			// if not in pending and not in active then the window was closed.
			if (this.pendingWindows[baseDescriptorName] && !activeWindows.getWindow(baseDescriptorName)) {
				// console.warn("Failed To Launch " + baseDescriptor.name + " " + retryAttempt);
				if (retryAttempt > 2) {
					// This is where failed windows used to be force closed and a respawn attempted but on some systems windows take a long time to load and force closing them or retrying spawning was causing problems. For now, just warn if things are taking too long.
					// Attempting to wrap the window while loading here was also taking a really long time. Just letting things take their course seems to eventually work.
					Logger.system.warn("Window Taking Really Long to Load:", baseDescriptorName);
					// in theory should never get to this point, but do.  So clear pending flag otherwise will never be able to spawn this component again until a restart (because always pending)
					delete this.pendingWindows[baseDescriptorName];
					clearInterval(interval);
				}
			} else {
				clearInterval(interval);
			}
		}, 5000);
	}

	// when spawning initialize related config for a new component and adjust titlebar portion of config
	private initConfigAndAdjustForTitleBar(config, FSBLHeader, titleBarHeight, titlebarType) {
		// If the window has a titlebar check if a titlebar height is defined locally, or globally.
		// Priority is local > global > default. Set the local config to this value as this config is passed down to fea.
		if (FSBLHeader !== false) {
			const DEFAULT_TITLEBAR_HEIGHT = 25;

			// Ensure that we have a complete nested object so that we can assign values to it below
			config = merge(UNKNOWN_DEFAULT_CONFIG, config ?? {});

			if (!titleBarHeight || !Number.isInteger(titleBarHeight)) {
				({ titleBarHeight } = this.finsembleConfig["Window Manager"]);
				if (!titleBarHeight || !Number.isInteger(titleBarHeight)) {
					titleBarHeight = DEFAULT_TITLEBAR_HEIGHT;
				}
				config.foreign.components!["Window Manager"]!.titleBarHeight = titleBarHeight;
			}

			config.foreign.components!["Window Manager"]!.titlebarType = titlebarType;
		}

		return config;
	}

	// checks the ability to spawn Toolbar with previous bounds
	private isSavedToolbarBoundsAvailable(savedBounds, monitors) {
		if (!savedBounds || !monitors) return false;

		// If saved bounds belong to one of the monitor return `true`
		return Array.from(monitors).some(
			(monitor: any) =>
				savedBounds.left >= monitor.availableRect.left &&
				savedBounds.right <= monitor.availableRect.right &&
				savedBounds.top >= monitor.availableRect.top &&
				savedBounds.bottom <= monitor.availableRect.bottom
		);
	}

	// Workaround for Chromium bug, https://github.com/electron/electron/issues/22840, which causes toolbar to be invisible in some monitor configurations.
	async addToolbarVisibilityBugWorkaround(name, config, adjustedSpawnParams, windowDescriptor) {
		// The problem happens if a dockable component like the Toolbar is first spawned in default location on Primary monitor before being moved to correct location in DockableWindows.
		// The Solution here is to sidestep the Chromium bug by initially spawning the dockable component on the in the correct location (which makes sense anyway but not the way it previously worked).
		// Note 1: It's worth acknowledging this is not a clean solution because related behavior is split between here (pre-spawn) and DockingWindow (post-spawn), but minimizing the extend of the change.
		if (adjustedSpawnParams.dockable || adjustedSpawnParams.component == "Toolbar") {
			// dockable windows need to be invisible when spawned so they can be moved to the proper docked location.
			windowDescriptor.autoShow = false;

			// Get the dockable window's correct location (i.e. the last position that is saved in storage) -- it won't always exist.
			const { data: storageData } = await StorageClient.getStandardized({
				topic: name,
				key: name,
			});
			const dashbarEnabled = !!(config?.window.data && config.window.data.dashbar && config.window.data.dashbar.height);
			//If the window has a dashbar, set the appropriate maxHeight/maxWidth
			if (dashbarEnabled && config) {
				config.window.maxHeight = config?.window.height + config.window.data.dashbar.height;
				windowDescriptor.maxHeight = config.window.maxHeight;
				windowDescriptor.defaultHeight = windowDescriptor.maxHeight;
				//Currently the dashbar has a fixed vertical height, so min height should be the same as maxheight
				windowDescriptor.minHeight = config.window.maxHeight;
			}

			// updates the AoT state from the storage
			if (storageData?.alwaysOnTop !== undefined) {
				windowDescriptor.alwaysOnTop = storageData.alwaysOnTop;
			}

			// if previous location is known, then change windowDescriptor so the component is spawned in that location.
			if (storageData?.["window-bounds"]) {
				// reset Toolbar bounds if the previous position is unavailable
				if (!this.isSavedToolbarBoundsAvailable(storageData["window-bounds"], adjustedSpawnParams.monitors)) {
					StorageClient.remove({
						topic: name,
						key: name,
					});
					return;
				}
				// Note 2: this fix didn't work unless BOTH the bound and the default bound are set (e.g. top and defaultTop), which is another known problem
				// Note 3: the underlying bug still sometimes happened unless component location first set to the top of the correct monitor (top=0) then finally positioned in the DockingWindow
				windowDescriptor.top = windowDescriptor.defaultTop = storageData["window-bounds"].top;
				windowDescriptor.left = windowDescriptor.defaultLeft = storageData["window-bounds"].left;
				windowDescriptor.right = storageData["window-bounds"].right;
				windowDescriptor.width = windowDescriptor.defaultWidth = storageData["window-bounds"].width;
				if (typeof storageData?.["dashbarEnabled"] === "boolean") {
					// Handle transitioning between dashbar being enabled/disabled. Because some values come in from the store
					// we need to overwrite values that change based on dashbar state if the dashbar has been enabled or disabled since the last time
					// the store was saved. If we don't do this the toolbar won't transform fully until a reset.
					if (dashbarEnabled === storageData["dashbarEnabled"]) {
						// dashbar state matches in the store and config
						windowDescriptor.bottom = windowDescriptor.bottom = storageData["window-bounds"].bottom;
						windowDescriptor.height = windowDescriptor.defaultHeight = storageData["window-bounds"].height;
					} else {
						const { top = 0, defaultHeight = 480 } = windowDescriptor;
						// dashbar state has changed. Update the store with bounds for the new toolbar type
						storageData["window-bounds"].height = defaultHeight;
						storageData["window-bounds"].bottom = top + defaultHeight;
						StorageClient.save({
							topic: name,
							key: name,
							value: storageData,
						});
					}
				}
				Logger.system.log(
					"PRESETTING-BOUNDS spawn: ",
					"windowDescriptor",
					"params",
					adjustedSpawnParams,
					"windowDescriptor",
					windowDescriptor,
					"storageData",
					storageData
				);
			}
		}
	}

	// after all the data has been pulled together in spawn, now spawn the new window
	async spawnWithUpdatedData(
		config,
		windowDescriptor,
		baseDescriptor,
		adjustedSpawnParams,
		addToWorkspace
	): Promise<{ errorString: string; descriptor: WindowDescriptor }> {
		let errorString: any = null;
		let descriptor: any = null;
		const { name = "" } = windowDescriptor;

		this.preSpawnHandleMinMaxWindowState(adjustedSpawnParams, windowDescriptor);

		await this.addToolbarVisibilityBugWorkaround(name, config, adjustedSpawnParams, windowDescriptor);

		// Sometimes users can pass unreasonable positional parameters when spawning a component.
		// For really extreme values, it can render Finsemble completely unusable and unstable, therefore
		// in here we force the component to have a sensible size that will fit in the monitor in which the
		// component is expected to spawn (also bring the component to the monitor if it is spawning outside of it).
		const { availableRect }: any = await getMonitorFromWindow(windowDescriptor);
		// If the spawning component is wider or higher than the available rect, then adjust the bounds.
		if (
			windowDescriptor.defaultWidth &&
			windowDescriptor.defaultHeight &&
			(availableRect.width < windowDescriptor.defaultWidth || availableRect.height < windowDescriptor.defaultHeight)
		) {
			this.updateWindowDescriptorBounds(windowDescriptor);
		}

		// Ensure the store has the WindowDescriptor before we actually spawn the window
		this.store.dispatch(
			actions.addLaunchData({
				windowDescriptor,
				tabTitleFromSpawn: adjustedSpawnParams.tabTitle,
				workspaceWindow: addToWorkspace || adjustedSpawnParams.spawnedByWorkspaceService,
				finsembleConfig: this.finsembleConfig,
				windowDotOpen: false,
			})
		);

		// Ensure the store has the default window data before we actually spawn the window
		this.store.dispatch(actions.createDefaultWindowTitleBar(windowDescriptor.name as string));

		const spawnResult = await self.doSpawn(windowDescriptor);
		const { err, windowIdentifier: newWindowIdentifier } = spawnResult;
		Logger.system.info(windowDescriptor.name, "Inside LauncherService.spawn(), before ");

		if (err) {
			errorString = err;
		} else {
			/**
			 * Add to the workspace *if* the caller wants it added (for instance from the app launcher)
			 * but *also* if the defaultComponentConfig for the component allows it to be added to workspaces (defaults to true)
			 *
			 * MB 7/16/20 Adding the window to the workspace was moved after spawn to handle a bug where save was executed
			 * before a window finished spawning. The window should only be added to the workspace when it has full data available.
			 * The window must be added to the workspace before the call to finishSpawn as finalizing the window updates the workspace data.
			 */
			if (addToWorkspace) {
				const workspaceData = await BaseWindow.cleanupState({
					...windowDescriptor,
					launchGroupComponentData: windowDescriptor.launchGroupComponentData,
				});

				// If component state is provided in the spawnParams, send it to the workspace service.
				workspaceData.componentState = adjustedSpawnParams.componentState ?? {};

				try {
					await addWindowReferenceToActiveWorkspace(workspaceData);
				} catch (addWindowError) {
					Logger.system.error(`There was an error adding ${windowDescriptor.name} to the workspace`, addWindowError);
				}
			} else {
				// Unset the user specified title if addToWorkspace is not true. Saving this value to the workspace relies on setComponentState which is not available until after spawn.
				// By unsetting this value here (after the title has been updated) we can avoid requiring addToWorkspace in finishSpawn
				adjustedSpawnParams.tabTitle = null;
			}

			const { err: innerErr, data } = await self.finishSpawn(
				config ?? UNKNOWN_DEFAULT_CONFIG,
				windowDescriptor,
				adjustedSpawnParams,
				newWindowIdentifier
			);
			if (innerErr) {
				errorString = innerErr;
			} else {
				descriptor = data;
			}
			Logger.system.info(windowDescriptor.name, "Inside LauncherService.spawn(), after ");
		}
		if (errorString) {
			Logger.system.error(errorString);
		}

		return { errorString, descriptor };
	}

	/*
	 *	Show the evergreen window
	 */
	showEvergreenWindow(finWindow: FSBLWindow): Promise<void> {
		Logger.system.debug("Launcher - launcher.spawnEvergreenList - showEvergreenWindow", finWindow.name);

		return new Promise((resolve) => {
			finWindow._show({}, () => {
				finWindow._bringToFront({}, () => {
					this.pendingEvergreenWindows.splice(
						this.pendingEvergreenWindows.findIndex((n) => n === finWindow.name),
						1
					);
					Logger.system.debug(
						"Launcher - launcher.spawnEvergreenList - remove from pending windows list",
						finWindow.name
					);
					resolve();
				});
			});
		});
	}

	/*
	 *	Save evergreen window in the active workspace
	 */
	async addEvergreenWindowToWorkspace(params, component, finWindow: FSBLWindow) {
		const workspaceData = await BaseWindow.cleanupState({
			...params,
			isEvergreen: true,
			componentType: component,
			name: finWindow.name,
		});
		try {
			await addWindowReferenceToActiveWorkspace(workspaceData);
		} catch (addWindowError) {
			Logger.system.error(`There was an error adding ${finWindow.name} to the workspace`, addWindowError);
		}

		// A window already exists and we're "spawning" an new one. We need to reset the linker state
		RouterClient.transmit("LinkerService-Event-component-state-changed", {
			windowName: finWindow.name,
			Finsemble_Linker: {},
		});

		// We're "spawning" an new window. Reset the component state
		finWindow.eventManager.trigger("component-state-changed", {});
	}

	/**
	 * Search for an existing evergreen window.
	 * Only used when spawning a new evergreen window, allowing reuse of a currently unused window.
	 * If the window is a singleton then the first instance encountered is returned (as it should be the only one).
	 * If a window name is provided and an evergreen window with the same name exists it will be returned.
	 * Otherwise a random hidden evergreen window is returned or `undefined` if none exists, indicating that a new one should be spawned.
	 *
	 * @param existingWindows An array of existing windows with the same component type as the search window
	 * @param evergreenWindowName the name of search window
	 * @param isSingleton config parameter indicating whether the window is a singleton.
	 * @returns `FSBLWindow` or `undefined` if the window is not found.
	 */
	async findExistingHiddenEvergreen(
		existingWindows: FSBLWindow[],
		evergreenWindowName?: string,
		isSingleton?: boolean
	) {
		let selectedWindow: FSBLWindow | undefined = undefined;
		// Check for an existing evergreen window with the right name first.
		if (evergreenWindowName) {
			selectedWindow = existingWindows.find((w) => w.name === evergreenWindowName);
			if (selectedWindow) {
				return selectedWindow;
			}
		}

		const state = this.store.getState();

		for (let i = 0; i < existingWindows.length; ++i) {
			selectedWindow = existingWindows[i];
			// Exclude windows that are already showing as they are in use already
			// except for singleton windows, where we *have* to reuse them.
			// There is not isShowing function in NativeWindow, so use base function
			const visible = selectedWindow.isShowing
				? await promisify<boolean>(selectedWindow.isShowing)({})
				: await selectedWindow._isShowing({});

			// Handle unselected tabs in tabbed windows (as these will also appear to be not showing)
			//    consider any window in a tabCollection to be in use
			//    (as we remove from tabCollections on workspace close)
			if (!visible && state.windowTitleBar.windows[selectedWindow.name]?.tabCollectionId) {
				selectedWindow = undefined;
				continue;
			}

			// If the evergreen component is visible or is part of a stack, it can't be reused unless it is a
			// singleton component
			if (visible || this.pendingEvergreenWindows.includes(selectedWindow.name)) {
				if (isSingleton) {
					return selectedWindow;
				} else {
					selectedWindow = undefined;
					// Look for first instance of a hidden evergreen component
					continue;
				}
			} else {
				this.pendingEvergreenWindows.push(selectedWindow.name);
				return selectedWindow;
			}
		}

		return selectedWindow;
	}

	/**
	 * Show the component if it is existing evergreen component. This function only gets called called on spawn and does
	 * not get called on workspace restore.
	 * @return true if handled and nothing else needs to be done; otherwise false
	 */
	async showIfExistingEvergreen(params, config, component, cb) {
		const existingWindows = activeWindows.findAllByComponentType(component);
		if (existingWindows.length) {
			Logger.system.debug(
				`Launcher.spawn 3: Found existing windows for evergreen component '${component}'`,
				existingWindows
			);

			// If Evergreen is spawned in a second workspace, check if we need to store data in this new workspace
			const { data } = await WorkspaceClient.getActiveWorkspace();
			const activeWSComponents = data.windows;

			const evergreenWindowName = params?.options?.name;
			const isSingleton = config.component?.singleton;

			const selectedWindow = await this.findExistingHiddenEvergreen(existingWindows, evergreenWindowName, isSingleton);
			const spawnedByWorkspaceService = params.launchingWindow?.windowName === "workspaceService";

			const maybeUpdateActiveWorkspace = async (window: FSBLWindow) => {
				if (!activeWSComponents.includes(window.name) && !spawnedByWorkspaceService) {
					params = this._removeDisallowedSpawnParams(params);
					params = merge(config.window, params, params.options);
					await this.addEvergreenWindowToWorkspace(window.windowOptions, component, window);
				}
			};

			// restores window state in case when the not normal evergreen window is spawned in the second workspace
			if (selectedWindow && selectedWindow.windowState !== WINDOWSTATE.NORMAL) {
				await selectedWindow._restore({});
			}

			if (selectedWindow && isSingleton) {
				await maybeUpdateActiveWorkspace(selectedWindow);
				await this.showEvergreenWindow(selectedWindow);

				cb?.(null, {
					windowDescriptor: selectedWindow.windowDescriptor,
					windowIdentifier: selectedWindow.identifier,
				});
				return true;
			} else if (selectedWindow) {
				await maybeUpdateActiveWorkspace(selectedWindow);

				// The below fixes the error that tries to get bounds on the workspace services
				// when switching back to a workspace and the window is already open but hidden
				// ShowWindow however behaves strangely with evergreens on different monitors
				// on different workspaces.
				if (spawnedByWorkspaceService) {
					params.launchingWindow = selectedWindow.identifier;
					params.left = params.options.left;
					params.top = params.options.top;
					params.width = params.options.width;
					params.height = params.options.height;
				}

				// Alternative to showWindow - works cross monitor
				// sometimes params bounds is undefined
				!isNaN(params.left) &&
					!isNaN(params.top) &&
					!isNaN(params.width) &&
					!isNaN(params.height) &&
					selectedWindow._setBounds({
						bounds: {
							left: params.options.left,
							top: params.options.top,
							width: params.options.width,
							height: params.options.height,
						},
					});

				await this.showEvergreenWindow(selectedWindow);

				cb(null, {
					windowDescriptor: selectedWindow.windowDescriptor,
					windowIdentifier: selectedWindow.identifier,
				});
				return true;
			}

			// if we didn't find a hidden evergreen and the workspace already includes a window name, we should remove it to spawn a new evergreen component and prevent names collision
			if (evergreenWindowName && activeWSComponents.includes(params.options.name)) {
				params.options.name = undefined;
			}
		}
		return false;
	}

	/**
	 *
	 * @param componentType
	 * @returns true if the component is in the process of being spawned
	 */
	isPendingComponent(componentType: string) {
		for (const p in this.pendingWindows) {
			const pendingWindow = this.pendingWindows[p];
			if (pendingWindow.componentType === componentType) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Show the component if it is existing singleton
	 * @return true if handled and nothing else needs to be done; otherwise false
	 */
	async showIfExistingSingleton(params, config, component, cb) {
		// we should check 4 cases:
		// 1. singleton & evergreen			- always return false if the component is spawned at first time
		// 2. singleton & not evergreen		- return true if singleton component already exists or in the process of being spawned
		// 3. not singleton & evergreen		- return true if evergreen component already exists
		// 4. not singleton & not evergreen - always return false
		Logger.system.debug("Launcher.spawn 3", component, params);

		const isSingleton = !!config?.component?.singleton;
		const isEvergreen = !!config?.window?.options?.isEvergreen;

		if (isSingleton) {
			// Workaround to handle a case when multiple singleton components are spawned at the same time.
			// We have a delay between a singleton check and adding component to pending windows
			// what means more then one singleton component can get through the check.
			// Code below eliminates the effect of this delay.
			if (!this.pendingSingletonWindows[component]) {
				this.pendingSingletonWindows[component] = new Promise((resolve) => {
					const interval = setInterval(() => {
						if (!this.pendingSingletonWindows[component]) {
							clearInterval(interval);
							resolve();
						}
					}, 50);
				});
			} else {
				await this.pendingSingletonWindows[component];
			}

			if (this.isPendingComponent(component)) {
				cb(`A window for this singleton component is in the process of being spawned`);
				// case 2
				return true;
			}

			// case 1
			if (isEvergreen) {
				return await this.showIfExistingEvergreen(params, config, component, cb);
			}

			const existingWindows = activeWindows.findAllByComponentType(component);
			if (existingWindows.length) {
				Logger.system.debug(
					`Launcher.spawn 3: Found existing windows for singleton component '${component}'`,
					existingWindows
				);

				// assume there is only one existing window (its a singleton after all) call showWindow
				// on it to bring to front and apply any positioning or sizing specified in the spawn params
				await this.showWindow({ windowName: existingWindows[0].name }, params, cb);
				// case 2
				return true;
			}
		}

		// case 3
		if (!isSingleton && isEvergreen) {
			return await this.showIfExistingEvergreen(params, config, component, cb);
		}

		// cases 2 & 4
		return false;
	}

	// Partially pull together config and component data for spawning
	private updateInitialSpawnConfigAndComponent(config, params, component) {
		if (!config) {
			if (get(params, "options.customData.component.isUserDefined")) {
				config = params.options.customData;
			} else if (params.url) {
				// No config, but has as URL. Treat as an ad-hoc component. This path is hit when using using window.open
				// from nativeOverrides.js

				// System will bomb if a component name has periods, the distributed store does
				// some string splitting on periods because of internal identifiers. (e.g. [...].Finsemble.[...])
				component = params.name ? params.name.replace(/\./g, "-") : params.url.replace(/\./g, "-");
				config = null;
			} else {
				// Use a config to drive what component is shown if we can't find one in our list
				const unknownComponentName = this.getUnknownComponentName() || "404";
				const unknownConfig = this.getDefaultConfig(unknownComponentName) || UNKNOWN_DEFAULT_CONFIG;
				config = {
					window: unknownConfig.window,
					component: {
						type: component,
						isUnknownComponent: true,
					},
					components: {},
					foreign: {},
				};
				component = params.name;
				set(params, "options.url", get(config.window, "url"));
				Logger.system.warn(
					`No config found for component "${component}". The URL for this component will be set to the unknown component URL. When the config is restored, the URL will be reset.`
				);
			}
		}

		// isEvergreen means that the component is addToWorkspace true
		if (config?.window?.options?.isEvergreen) {
			const evergreenConfig = {
				window: {
					addToWorkspace: true,
				},
			};
			config = merge(config, evergreenConfig);
		}

		return { config, component };
	}

	// Attempt to handle situations where the user requests to group a window on spawn but may pass in a location that ends up not adjacent.
	private updateParamsForAdjacentSideEffects(params) {
		// There are several spawn params that can conflict which each other, and we currently don't have much logic to choose "winners"
		// The main goal here is to prevent spawning windows in non-adjacent locations and grouping them. At least one bound must be set to adjacent or this is
		// not likely to happen, so we will bail on the group in this case.
		// If it seems valid to group at this point, we set params.position to be relative to prevent other strange behavior. Two windows need to be relative
		// to be validly grouped, and our default value when this is unset can allow grouped windows to be separated from each other.
		const adjacentSideExists =
			params.left === "adjacent" ||
			params.right === "adjacent" ||
			params.top === "adjacent" ||
			params.bottom === "adjacent";
		if (adjacentSideExists && params.canGroup !== false && (params.dockOnSpawn || params.groupOnSpawn)) {
			params.position = "relative";
		}
		return params;
	}

	// when spawning new window, update descriptor before it is save in workspace
	private updateDescriptorForWorkspace(config, adjustedSpawnParams, windowDescriptor) {
		const persistURL =
			config?.foreign?.services?.workspaceService?.persistURL ??
			config?.foreign?.services?.workspace?.persistURL ??
			this.persistURL ??
			false;

		const persistPath =
			config?.foreign?.services?.workspaceService?.persistPath ??
			config?.foreign?.services?.workspace?.persistPath ??
			this.persistPath ??
			false;

		/**
		 * We store the fact that a component had its URL swapped with the unknown component URL
		 * on the windowDescriptor itself. Therefore, if that prop is true, we need to swap the
		 * current URL with the one found in config. This logic will likely need to remain here
		 * (where we have access to the component's config), regardless of where the persistURL
		 * logic lands. */
		const isUnknownComponent = get(adjustedSpawnParams, "options.customData.component.isUnknownComponent");
		if (!persistURL || isUnknownComponent) {
			windowDescriptor.url = config?.window.url;
		}
		if (!persistPath || isUnknownComponent) {
			windowDescriptor.path = config?.window.path;
		}
		return windowDescriptor;
	}

	// remove preloads from spawn params so they aren't saved in workspace
	private stripPreloadOptionsFromParams(params: SpawnParams) {
		// This fixes a bug in our workspace save. We save everything and preload scripts in here overwrites what we actually want to use.
		// Loop through any preload scripts and remove FSBL
		if (params.options?.preloadScripts && Array.isArray(params.options.preloadScripts)) {
			for (let i = 0; i < params.options.preloadScripts.length; i++) {
				const preloadItem = params.options.preloadScripts[i];
				if (preloadItem?.url.indexOf(".FSBL.js") >= 0) {
					delete params.options.preloadScripts[i];
				}
			}
			params.options.preloadScripts = params.options.preloadScripts.filter((preload) => preload);
		}
		return params;
	}

	// get baseDescriptor values for spawning a new window
	private getBaseDescriptor(descriptorData) {
		let { params, config, FSBLHeader, component, descriptorName, disableBrowserView } = descriptorData;

		let baseDescriptor = new LauncherDefaults().windowDescriptor;
		if (params.options) {
			baseDescriptor = merge(baseDescriptor, params.options);
		}

		// hideOnce is set by the workspaceService when spawning hidden tabs
		baseDescriptor.hideOnce = params.hideOnce;
		baseDescriptor.name = descriptorName;
		baseDescriptor.componentType = component;
		baseDescriptor.customData.component.type = component;
		// windowInterop is used by InteropService for auto-associations. Maintaining in window descriptor allows it to be read by the InteropService
		baseDescriptor.windowInterop = params.windowInterop ?? {};
		// launchingWindow is set in launcher client and used here as the default windowInterop parent
		baseDescriptor.windowInterop.parent = baseDescriptor.windowInterop.parent ?? params.launchingWindow;
		// the connectId can also be a spawn parameter, so if specified copy to windowInterop (unless already in windowInterop)
		baseDescriptor.windowInterop.connectId = baseDescriptor.windowInterop.connectId ?? params.connectId;

		if (this.finsembleConfig.system.finsembleLibraryPath) {
			baseDescriptor.preloadScripts = [{ url: this.finsembleConfig.system.finsembleLibraryPath }];
		} else {
			baseDescriptor.preloadScripts = [];
		}

		baseDescriptor.preloadScripts = this.getPreloadScripts(
			config,
			baseDescriptor.preloadScripts,
			FSBLHeader,
			disableBrowserView
		);
		baseDescriptor.preload = baseDescriptor.preloadScripts;

		// url overrides the default component url (and can also be used to simply spawn a url). Ignore if spawned by workspace otherwise it will overwrite the url from workspace. This is dealt with at a later point with a check for the persistURL config item.
		if (params.url && !params.spawnedByWorkspaceService) {
			baseDescriptor.url = params.url;
		}

		return baseDescriptor;
	}

	// returns window descriptor name for new window being spawned
	private getSpawnedWindowDescriptorName(params, config, component) {
		let descriptorName;
		// System will bomb if a component name has periods, the distributed store does
		// some string splitting on periods because of internal identifiers. (e.g. [...].Finsemble.[...])
		// Also is used in the intermediateUpdateParamsUsingConfig function
		const componentWithoutPeriod = component?.replace(/\./g, "-");
		// Spawning a (singleton only) evergreen component in a new workspace will generate a unique name which
		// will conflict with others that haven't yet spawned from a different workspace.
		if (params.addToWorkspace && !config?.window?.options?.isEvergreen) {
			descriptorName = params.name ? params.name : `${componentWithoutPeriod}-${uuidv4()}`;
		} else {
			descriptorName = params.name ? params.name : this.getSequentialName(componentWithoutPeriod);
		}

		// Let the router know the name of the component being spawned
		// @deprecated, REMOVE in 7.0
		RouterClient.transmit("Launcher.spawningComponent", {
			name: descriptorName,
		});
		return descriptorName;
	}

	// after initial spawn() processing, update the params using config values and adjust positioning
	private intermediateUpdateParamsUsingConfig(params, config) {
		// window config from json is the default. params argument overrides.
		params = merge(config?.window, params);

		// This prevents invalid positions to be passed in which was causing odd behaviors spawning windows.
		// [ASSUMPTION] - The default 'params.position' value will be 'available' (e.g. in case the position is
		// 	undefined or an invalid value)
		if (!_isOfPositionTypes(params.position ?? "available")) {
			params.position = "available";
		}

		// if we have right|bottom and width|height we don't need default left|top="center"
		const hasRightAndWidth = (params.right || params.right === 0) && params.width && params.width !== 0;
		const hasBottomAndHeight = (params.bottom || params.bottom === 0) && params.height && params.height !== 0;
		if (params.left === "center" && hasRightAndWidth) {
			delete params.left;
		}
		if (params.top === "center" && hasBottomAndHeight) {
			delete params.top;
		}

		// System will bomb if a component name has periods, the distributed store does
		// some string splitting on periods because of internal identifiers. (e.g. [...].Finsemble.[...])
		if (params.options && params.options.name) params.name = params.options.name.replace(/\./g, "-");

		return params;
	}

	/**
	 * Launches a component, either though LauncherClient API or for internal use.
	 * @param {object} params See LauncherClient
	 * @param {function} cb Callback
	 */
	async spawn(params: SpawnParams, cb: StandardCallback) {
		let { component } = params;

		// Maybe replace the component name if it's a legacy UI component. See comment in ../../../../deprecated/legacyUIComponentNames.ts
		component = legacyUINameMap[component] ?? component;
		Logger.system.debug("Launcher.spawn", component, params);

		// This fixes a bug in our workspace save.
		params = this.stripPreloadOptionsFromParams(params);
		let config = this.getDefaultConfig(params.component);

		if (this.shuttingDown) {
			// If the Launcher is shutting down, don't allow new components to come online. If the dev wants components to come up, they should spawn them prior to transmitting the shutdownList request.
			Logger.system.log("Dropping spawn request. Application is shutting down", component, params);
		} else if (component && !(typeof component === "string" || component instanceof String)) {
			// if component is not a string then we are trying to spawn multiple components (this is still experimental and not yet used - it is planned so that group launches get easier via pins etc.)
			this.spawnGroup(component, params, cb);
		} else if (config && _isLaunchGroupComponent(config)) {
			// If the configuration has sub-components, means that this component is a launchGroup component
			// i.e. it will spawn other components (either tabbed component, group or both)
			self.spawnLaunchGroupComponent(params, config, cb);
		} else {
			await this.spawnSingleComponent(params, config, component, cb);
		}
	}

	/**
	 * Launches a single component
	 *
	 * @param params spawn parameters
	 * @param config default config for the component being spawned
	 * @param component component name to spawn
	 * @param cb
	 * @memberof Launcher
	 */
	async spawnSingleComponent(
		params: SpawnParams,
		config: ComponentConfig | null,
		component: any,
		cb: StandardCallback
	) {
		Logger.system.debug("Launcher.spawnSingleComponent", params, config, component);
		Logger.perf.debug("Spawn", "start", component, params);

		Logger.system.debug("Launcher.spawn 2", component, params);

		({ config, component } = this.updateInitialSpawnConfigAndComponent(config, params, component));

		// handle existing singleton, including evergreen component
		if (component && (await this.showIfExistingSingleton(params, config, component, cb))) {
			delete this.pendingSingletonWindows[component];
			// don't continue if handled as an existing singleton
			return;
		}

		// cache the positioning params before they might be modified
		const requestedPositioning = {
			left: params.left,
			right: params.right,
			top: params.top,
			bottom: params.bottom,
		};

		params = this._removeDisallowedSpawnParams(params);

		params = this.intermediateUpdateParamsUsingConfig(params, config);

		let descriptorName = this.getSpawnedWindowDescriptorName(params, config, component);

		// Although all windows being spawned will not need an authentication token, it's easier to always generate than to discern when needed.
		const authenticationToken = generateDynamicAuthenticationToken(descriptorName, params.component);

		params.options = updateCustomDataWithAuthToken(params.options, authenticationToken);

		// Never add Citrix components to workspaces
		const addToWorkspace =
			(params.addToWorkspace ?? false) && config?.window?.addToWorkspace !== false && params.windowType !== "Citrix";

		// restore cached positioning params
		if (
			requestedPositioning.left ||
			requestedPositioning.right ||
			requestedPositioning.top ||
			requestedPositioning.bottom
		) {
			params.left = requestedPositioning.left;
			params.right = requestedPositioning.right;
			params.top = requestedPositioning.top;
			params.bottom = requestedPositioning.bottom;
		}

		// If we're set to spawnOnAllMonitors then we're going to call spawn() recursively, but
		// setting the monitor for each one. Note that since this is re-entrant, we need to make
		// sure we don't create an infinite loop! If params.monitor is set to anything other than "all"
		// then we bypass this.
		if (params.monitor === "all" || (config?.component?.spawnOnAllMonitors && typeof params.monitor === "undefined")) {
			this.spawnOnAllMonitors(component, params, { descriptorName, addToWorkspace }, cb);
			return;
		}

		const windowManager = get(config, ["foreign", "components", "Window Manager"], {});

		const titlebarType = _determineTitleBarType(this.finsembleConfig, config, component);

		// We do not destructure here because:
		// * the "windowManager" type declares titleBarHight as optional
		// * the "windowManager" type does not have FSBLHeader
		//
		// Property 'FSBLHeader' does not exist on type '{} |
		//	{
		// 	    [k: string]: any;
		// 	    launchableByUser?: boolean | undefined;
		//      titleBarHeight?: number | undefined;
		//      disableBrowserView?: boolean | undefined;
		//    	titlebarType?: string | undefined;
		//	}'
		//  ts(2339)

		// eslint-disable-next-line prefer-destructuring
		let titleBarHeight = windowManager["titleBarHeight"];
		// eslint-disable-next-line prefer-destructuring
		let FSBLHeader = windowManager["FSBLHeader"];

		let baseDescriptor = this.getBaseDescriptor({
			params,
			config,
			FSBLHeader,
			component,
			descriptorName,
		});

		Logger.system.debug("Launcher.spawn 4", component, params, baseDescriptor);

		// in theory should never have to cleanup pendingWindows map, but do for failed windows that will be respawned.
		this.cleanupPendingWindowsMap(baseDescriptor.name);

		// Construct an object of values that may be used to calculate the windowType
		const winConfig = {
			windowType: params.windowType,
			compound: baseDescriptor.compound,
			type: baseDescriptor.type,
		};

		params.windowType = util.getWindowType(winConfig) as WindowTypes;
		baseDescriptor.windowType = params.windowType;

		Logger.system.debug("Launcher.spawn 5", component, params);

		if (NATIVE_WINDOW_TYPES.includes(params.windowType)) {
			baseDescriptor.alias = params.alias;
			baseDescriptor.path = params.path;
			baseDescriptor.env = params.env;
			baseDescriptor.arguments = params.arguments;
		}

		/**
		 * This check needs doing before any async calls are made and the pending windows
		 * record needs to be created to avoid race conditions between concurrent spawn calls
		 */
		if (self.isWindowNameAlreadyUsed(descriptorName)) {
			const errorString = `Cannot spawn new window: windowDescriptor.name ${descriptorName} already used`;
			cb(errorString, null);
		} else {
			// save window properties of pending windows that are used later (e.g. to check dupes or for singleton windows) (will be removed in finishSpawn)
			this.pendingWindows[descriptorName] = {
				componentType: component,
				addToWorkspace: addToWorkspace ?? false,
				windowType: baseDescriptor.windowType,
			};

			config = this.initConfigAndAdjustForTitleBar(config, FSBLHeader, titleBarHeight, titlebarType);

			params = this.updateParamsForAdjacentSideEffects(params);

			const { windowDescriptor: newWindowDescriptor, adjustedSpawnParams } = await this.deriveBounds(params);
			let windowDescriptor = await self.compileWindowDescriptor(
				config ?? UNKNOWN_DEFAULT_CONFIG,
				adjustedSpawnParams,
				baseDescriptor,
				newWindowDescriptor
			);

			if (params.spawnedByWorkspaceService) {
				windowDescriptor = this.updateDescriptorForWorkspace(config, adjustedSpawnParams, windowDescriptor);
			}
			// Add canGroup to the windowDescriptor it makes it to the dockableWindow when it's created
			if (typeof adjustedSpawnParams.canGroup === "boolean") {
				windowDescriptor.canGroup = adjustedSpawnParams.canGroup;
			}

			// Set essential interop data now to ensure it will be available for FDC3 client registration withing the InteropService.
			// The windowDescriptor might still be modified within spawnWithUpdatedData, but not the properties needed for interop.
			this.interopAdmin.setInteropRegistrationData(windowDescriptor);

			// sets the real value of the addToWorkspace to the windowDescriptor
			(windowDescriptor as any).addToWorkspace = addToWorkspace;

			let { errorString, descriptor } = await this.spawnWithUpdatedData(
				config,
				windowDescriptor,
				baseDescriptor,
				adjustedSpawnParams,
				addToWorkspace
			);

			// remove pending singleton window after it has been added to regular pending windows and spawned
			delete this.pendingSingletonWindows[component];

			cb(errorString, descriptor);
		}
	}

	private updateWindowDescriptorBounds(windowDescriptor: WindowDescriptor) {
		// TPina - I really dislike this method even though I am its author.
		// It makes some assumptions and does not address the underlying issue (wrong calculations for spawn params) AND that
		// this whole launcher service should be re-written.
		// There are a lot of uncertainty about positional (absolute, relative, virtual, available, etc) values
		// and their meaning and behavior.

		// When this method is invoked the windowDescriptor already was updated with the calculations done
		// from the passed in positional values, however, for extreme values that would make a component spawn across
		// several monitors, we need to adjust the bounds of the component and overwrite some of the measurements.
		// The calculation update the default positional values in the windowDescriptor
		const newBounds = adjustBoundsToBeOnMonitor(this.monitorsInstance, {
			left: windowDescriptor.defaultLeft ?? 0,
			top: windowDescriptor.defaultTop ?? 0,
			height: windowDescriptor.defaultHeight ?? 0,
			width: windowDescriptor.defaultWidth ?? 0,
			right: 0,
			bottom: 0,
		});

		// update defaultLeft, defaultTop, defaultWidth and defaultHeight in the windowDescriptor
		windowDescriptor.defaultLeft = newBounds.left;
		windowDescriptor.defaultTop = newBounds.top;
		// Only update if the defaultWidth is bigger than the calculated width
		windowDescriptor.defaultWidth =
			windowDescriptor.defaultWidth && windowDescriptor.defaultWidth > newBounds.width
				? newBounds.width
				: windowDescriptor.defaultWidth;
		// Only update if the defaultHeight is bigger than the calculated height
		windowDescriptor.defaultHeight =
			windowDescriptor.defaultHeight && windowDescriptor.defaultHeight > newBounds.height
				? newBounds.height
				: windowDescriptor.defaultHeight;

		windowDescriptor.left = newBounds.left;
		windowDescriptor.top = newBounds.top;
		windowDescriptor.right = newBounds.left + newBounds.width;
		windowDescriptor.bottom = newBounds.top + newBounds.height;
	}

	getUnknownComponentName() {
		return this.finsembleConfig.servicesConfig?.launcher?.unknownComponent ?? null;
	}

	/**
	 * Launches a copy of the requested component on each of a user's monitors.
	 * @param {string} component The type of the component to launch
	 * @param {object} params See spawn.
	 * @param {function} cb Callback
	 */
	async spawnOnAllMonitors(
		component: ComponentConfig,
		params: SpawnParams,
		workspaceParams: { descriptorName: string; addToWorkspace?: boolean },
		cb: StandardErrorCallback
	) {
		// Gets all monitors and pushes a spawn call for each monitor to an array of async functions.
		this.getMonitorInfoAll((err, monitors) => {
			const tasks: any[] = [];
			monitors &&
				monitors.forEach((monitor) => {
					tasks.push(() => {
						const paramCopy = JSON.parse(JSON.stringify(params));
						paramCopy.monitor = monitor.position;
						paramCopy.component = component;
						self.spawn(paramCopy, async (spawnErr, result) => {
							if (!spawnErr) {
								result.windowDescriptor.spawnOnAllMonitors = true;
							}
							// Add to the workspace *if* the caller wants it added (for instance from the app launcher)
							// but *also* if the defaultComponentConfig for the component allows it to be added to workspaces (defaults to true)
							if (workspaceParams.addToWorkspace !== false) {
								const workspaceData = await BaseWindow.cleanupState({
									...params,
									name: workspaceParams.descriptorName,
								});
								try {
									await addWindowReferenceToActiveWorkspace(workspaceData);
								} catch (addWindowError) {
									Logger.system.error(
										`There was an error adding ${workspaceParams.descriptorName} to the workspace`,
										addWindowError
									);
								}
							}
						});
					});
				});
			asyncParallel(tasks, (parallelErr) => {
				cb(parallelErr);
			});
		});
	}

	/**
	 * Spawns an OF window, or sends a request to the native service to spawn a native window.
	 * Callback returns a handle to the new window
	 * @param {LauncherClient~windowDescriptor} windowDescriptor The descriptor to launch
	 * @param {function} cb Callback
	 */
	doSpawn(windowDescriptor: WindowDescriptor): Promise<{ err: string; windowIdentifier: WindowIdentifier }> {
		const promiseResolver = (resolve) => {
			this.createSplinterAndInject.createWindow({ windowDescriptor }, (err, windowIdentifier) => {
				Logger.system.debug("doSpawn createWindow", err, windowIdentifier, windowDescriptor);
				resolve({ err, windowIdentifier });
			});
		};
		return new Promise(promiseResolver);
	}

	/**
	 * Given an object where the keys are component names and the values are component configs, it spawns the list of components.
	 * @private */
	spawnGroup(components: ComponentConfig[], params: SpawnParams, cb: StandardErrorCallback) {
		let errors;
		const responses: any[] = [];
		const componentList = Object.keys(components);
		const spawnComponent = (componentType, done) => {
			let cloneParams = { ...params };
			if (components[componentType].params) {
				cloneParams = Object.assign(params, components[componentType].params);
			}
			cloneParams.component = componentType;
			self.spawn(cloneParams, (err, response) => {
				if (err) {
					errors.push(err);
				} else {
					responses.push(response);
				}
				done(err);
			});
		};
		each(componentList, spawnComponent, () => {
			cb(errors, responses);
		});
	}

	/**
	 *
	 * Spawns a series of windows then tabs them
	 */
	spawnTabCollection(
		{
			componentList,
			globalParams,
		}: {
			componentList: { appID: string; individualParams?: SpawnParams }[];
			globalParams?: TabbedGlobalSpawnParams;
		},
		cb: StandardErrorCallback<SpawnTabCollectionResponse>
	) {
		let failedSpawns;
		const windows: any[] = [];

		// Any params defined in TabbedGlobalSpawnParams will overwrite any matching individual params. This is because a tab collection needs to share
		// certain data such as position of the windows. Individual params will be disregarded for these values even if the global value is unset as the default
		// will be the values specified here.
		const spawnComponent = (component, index, done) => {
			const positionParams = {
				top: globalParams?.top ?? 0,
				left: globalParams?.left ?? 0,
				bottom: globalParams?.bottom,
				right: globalParams?.right,
				width: globalParams?.width,
				height: globalParams?.height,
				position: globalParams?.position ?? "available",
				launchingWindow: globalParams?.launchingWindow,
				addToWorkspace: globalParams?.addToWorkspace ?? true,
			};

			let individualParams = component?.individualParams ? JSON.parse(JSON.stringify(component?.individualParams)) : {};

			individualParams.component = component?.appID;
			Object.assign(individualParams, positionParams);

			if (!individualParams.options) {
				individualParams.options = {};
			}
			if (!individualParams.options.customData) {
				individualParams.options.customData = {};
			}

			self.spawn(individualParams, (err, response) => {
				if (err) {
					// Spawn already logs the specific error, maintain a list of the failed spawns to return to the user
					failedSpawns.push({ appId: component?.appID, position: index });
				} else {
					windows.push({
						response,
						windowName: response?.windowIdentifier?.windowName,
						position: index,
					});
				}
				done(err);
			});
		};
		asyncEachOf(componentList, spawnComponent, () => {
			let responses: SpawnTabCollectionResponse = {
				id: "",
				windows: [],
			};
			// Should we not form the tabCollection if any windows fail to spawn?
			if (windows?.length > 1) {
				// Enforce the tab order using the position in the appIds array. The windows are pushed to the results
				// array on spawn completion which may be different than the specified order.
				windows.sort((a, b) => a?.position - b?.position);
				const windowNames = windows.map((window) => window?.windowName);
				const promotedWindow = windows.find((window) => window?.position === globalParams?.promotedTabPosition);
				const tabCollectionId = `StackedWindow-${uuidv4()}`;
				responses = {
					id: tabCollectionId,
					windows: windows.map((window) => window?.response),
				};
				this.store.dispatch(
					actions.createTabCollection({
						tabCollectionId: tabCollectionId,
						windowNames: windowNames,
						promotedWindowName: promotedWindow?.windowName ?? windowNames[0],
					} as any)
				);
			}
			// Router only allows an error type of StandardError. Since we can't return an array of errors, create an error string
			// with information on which appIds failed to spawn. Position in the array is included since more than one window with the same
			// appId may have been requested.
			let errString;
			if (failedSpawns) {
				const failedSpawnList = failedSpawns?.map((win) => `${win.appId} at position: ${win.position}`).join(", ");
				errString = `Failed to spawn the following appIds in the tabCollection: ${failedSpawnList}`;
			}
			cb(errString, responses);
		});
	}

	/**
	 * Spawns a launchGroup component - i.e. a component made of other components such as tabbed component or a group.
	 *
	 * @param {Array.<string>} components - The components that form the tabbed group.
	 * @param {*} componentConfig - Object containing the component configuration.
	 * @param {Function} cb A callback function to be invoked after the window is spawned.
	 *
	 * @private
	 */
	async spawnLaunchGroupComponent(
		params: SpawnParams,
		componentConfig: ComponentConfig,
		cb: StandardErrorCallback = () => {}
	) {
		//merge configuration and spawn parameters
		const clonedConfig = structuredClone<ComponentConfig>(componentConfig);
		clonedConfig.window = Object.assign(clonedConfig.window ?? {}, params ?? {});

		const launchGroupComponentConfiguration = await processLaunchGroupComponentConfiguration(clonedConfig);

		const spawnResponses = await self.spawnLaunchGroup(params, launchGroupComponentConfiguration);
		const windowNames = spawnResponses.map((spawnResponse) => spawnResponse.windowIdentifier.windowName);

		// Instruct docking service to group the launch group windows by passing in the
		// group window names from the spawn responses
		RouterClient.query(
			"DockingService.groupWindows",
			{
				windows: windowNames,
				isMovable: true,
			},
			() => {
				cb(null, spawnResponses);
			}
		);
	}

	async updateEvergreenState(spawnParams, wrap: FSBLWindow) {
		Logger.system.debug("Launcher.updateEvergreenState", wrap.name, spawnParams);
		// update linker channels
		const { componentState } = spawnParams;
		let joinedChannels = null;
		if (componentState.length > 0 && componentState[0]?.Finsemble_Linker) {
			joinedChannels = componentState[0].Finsemble_Linker;
		} else if (componentState.length > 0 && componentState[0]["Interop-#Linker-linkedChannels"]) {
			joinedChannels = componentState[0]["Interop-#Linker-linkedChannels"]["channels"];
		}

		// updates linker channels when they were saved in workspace
		RouterClient.transmit("LinkerService-Event-component-state-changed", {
			windowName: wrap.name,
			Finsemble_Linker: joinedChannels,
		});

		// update component state
		await RouterClient.query(
			"WindowService-Request-componentStateChanged",
			{ windowIdentifier: wrap.identifier, componentState },
			() => {}
		);

		// update tab collection
		this.store.dispatch(
			actions.setTabCollectionForEvergreen({
				windowName: wrap.name,
				tabCollectionId: spawnParams.tabCollectionId,
			})
		);

		// update bounds
		const bounds = {
			left: spawnParams.left,
			top: spawnParams.top,
			width: spawnParams.width,
			height: spawnParams.height,
		};

		const updateBounds = async (newBounds = bounds) => {
			// use sendCommandToWindowService() instead of wrap._setBounds() to delegate to the Window service
			// the decision as to which wrapper a dockingWindow or regular to use for setBounds
			await sendCommandToWindowService(wrap.name, "setBounds", {
				bounds: newBounds,
			});
		};

		// the sleep function is required below to finish bounds updating on restore
		const sleep = (timeout) =>
			new Promise((resolve) => {
				setTimeout(resolve, timeout);
			});
		const spawnWindowState = spawnParams.options?.windowState;
		switch (spawnWindowState) {
			case WINDOWSTATE.MAXIMIZED:
				await wrap._restore({});
				await sleep(100);
				await updateBounds(spawnParams?.options?.cachedBounds ?? bounds);
				await wrap._maximize({});
				break;
			case WINDOWSTATE.NORMAL:
				// show the window before restoring to prevent jumping on resize
				await this.showEvergreenWindow(wrap);
				await wrap._restore({});
				await sleep(100);
				await updateBounds();
				break;
			case WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED:
				await wrap._restore({});
				await sleep(100);
				await wrap._minimize({});
				break;
			case WINDOWSTATE.MINIMIZED_WAS_NORMAL:
				await wrap._restore({});
				await sleep(100);
				await updateBounds();
				await wrap._minimize({});
				break;
			default:
				await updateBounds();
		}
	}

	/**
	 *
	 * @private
	 */
	async spawnEvergreenList(windowsSpawnParams: SpawnParams[]) {
		// contains pairs of evergreen names to spawn and actual spawned window name
		const namesMatching = new Map<string, string>();
		const componentTypes = windowsSpawnParams.map((windowData) => (windowData as any).options?.componentType);
		const uniqueComponentTypes = [...new Set(componentTypes)];

		const getEvergreenWIndowByName = (name: string) => windowsSpawnParams.find((win) => win.options?.name === name);

		const showExisting = async (existingWindow, evergreenWindow) => {
			Logger.system.debug(
				"Launcher - launcher.spawnEvergreenList - showExistingEvergreen",
				existingWindow.name,
				evergreenWindow.name
			);
			// for the native window first show then update the state, otherwise, the hidden native window doesn't restore the state
			if (existingWindow.windowType === "NativeWindow") {
				await this.showEvergreenWindow(existingWindow);
				await this.updateEvergreenState(evergreenWindow, existingWindow);
			} else {
				await this.updateEvergreenState(evergreenWindow, existingWindow);
				// show evergreen when it is not a hidden tab
				if (!evergreenWindow.hideOnce) {
					await this.showEvergreenWindow(existingWindow);
				} else {
					existingWindow?._hide();
				}
			}
		};

		// breakdown by component type
		await Promise.all(
			uniqueComponentTypes.map(async (component) => {
				const existingWindows = activeWindows.findAllByComponentType(component);
				const getExistingWindowByName = (name) => existingWindows.find((win) => win.name === name);
				let existingWindowsNames = existingWindows.map((w) => w.name);

				const evergreenWindows = windowsSpawnParams.filter(
					(windowData) => (windowData as any).options?.componentType === component
				);
				let evergreenWindowsNames = evergreenWindows.map((w) => w.options?.name ?? "");

				const evergreenToShow = evergreenWindowsNames.filter((name) => existingWindowsNames.includes(name));
				// show existing with name matching
				await Promise.all(
					evergreenToShow.map(async (name) => {
						const existingWindow = getExistingWindowByName(name);
						const evergreenWindow = getEvergreenWIndowByName(name);

						await showExisting(existingWindow, evergreenWindow);
						namesMatching.set(name, name);
					})
				);

				// removes already shown windows
				evergreenWindowsNames = evergreenWindowsNames.filter((name) => !evergreenToShow.includes(name));
				existingWindowsNames = existingWindowsNames.filter((name) => !evergreenToShow.includes(name));

				// re-uses existing evergreen which does not match by name
				while (existingWindowsNames.length > 0 && evergreenWindowsNames.length > 0) {
					const existingWindowName = existingWindowsNames[0];
					const evergreenWindowName = evergreenWindowsNames[0];
					const existingWindow = getExistingWindowByName(existingWindowName);
					const evergreenWindow = getEvergreenWIndowByName(evergreenWindowName);

					await showExisting(existingWindow, evergreenWindow);
					namesMatching.set(evergreenWindowName, existingWindowName);

					existingWindowsNames = existingWindowsNames.splice(1);
					evergreenWindowsNames = evergreenWindowsNames.splice(1);
				}

				// spawn the rest
				await Promise.all(
					evergreenWindowsNames.map(async (name) => {
						const evergreenWindow = getEvergreenWIndowByName(name);
						if (evergreenWindow) {
							let params = evergreenWindow;
							const componentToSpawn = legacyUINameMap[component] ?? component;
							params = this.stripPreloadOptionsFromParams(params);
							let config = this.getDefaultConfig(componentToSpawn);
							await this.spawnSingleComponent(params, config, componentToSpawn, async (error, response) => {
								const { windowIdentifier } = response;
								namesMatching.set(name, windowIdentifier.windowName);

								// workaround for FIN-1977
								// the spawning process may adjust window bounds to be on monitor
								// update the state to get the correct bounds
								const existingWindow = activeWindows.getWindow(windowIdentifier.windowName);
								await this.updateEvergreenState(evergreenWindow, existingWindow);
							});
						}
					})
				);
			})
		);

		return namesMatching;
	}

	/**
	 * For LaunchGroups, pull together and return the windowInterop params used by InteropService.
	 *
	 * @param {*} originalParams the original spawn parameters
	 * @param {*} launcherGroupComponentType component type of the LaunchGroup
	 * @param {*} launchGroupId LaunchGroup id
	 * @returns the interop params
	 * @memberof Launcher
	 */
	compileInteropParams(
		originalParams: SpawnParams,
		launcherGroupComponentType?: string,
		launchGroupId?: string
	): WindowInterop {
		const { connectId, windowInterop } = originalParams;

		// the parent for this case is the launch group
		const launchingWindow = {
			windowName: "system",
			componentType: launcherGroupComponentType,
		};

		// the launchGroup's parent is the grand parent;
		// when fdc3 InteropService is doing the spawn, the parent param is set and must be used here since launchingWindow is the InteropService (i.e. the wrong value).
		const grandParent = windowInterop?.parent ?? originalParams.launchingWindow;

		return {
			connectId,
			launchGroupId,
			parent: launchingWindow,
			grandParent,
		};
	}

	async spawnLaunchGroup(
		originalParams: SpawnParams,
		launchGroupComponentConfiguration: {
			componentsToSpawn: any;
			launcherGroupComponentType: string;
		}
	) {
		const { componentsToSpawn, launcherGroupComponentType } = launchGroupComponentConfiguration;
		const responses: any[] = [];
		// assign launchGroupId, which is used by Interop service to auto-associate all windows in group
		const launchGroupId = `launchGroupId-${uuidv4()}`;

		return Promise.all(
			componentsToSpawn.map(async (component) => {
				// if single component
				if (component.componentType) {
					const spawnParams = this._compileSpawnParams(component);
					if (spawnParams.launchGroupComponentData) {
						spawnParams.launchGroupComponentData.launchGroupId = launchGroupId;
					}
					// assign windowInterop, which is used by Interop service to auto-associate FDC3 clients
					spawnParams.windowInterop = this.compileInteropParams(
						originalParams,
						launcherGroupComponentType,
						launchGroupId
					);

					return new Promise((resolve) => {
						self.spawn(spawnParams, async (err, response) => {
							if (err) {
								Logger.system.error("spawnLaunchGroup - error spawning single component", err, component);
							} else {
								responses.push(response);
							}
							resolve(null);
						});
					});
				} else if (component.components) {
					//tabbed components
					const componentList = component.components.map((tab) => {
						const individualParams: SpawnParams = JSON.parse(JSON.stringify(tab?.spawnOptions));
						if (component.launchGroupComponentData) {
							component.launchGroupComponentData.launchGroupId = launchGroupId;
							individualParams.launchGroupComponentData = JSON.parse(
								JSON.stringify(component.launchGroupComponentData)
							);
						}

						individualParams.windowInterop = this.compileInteropParams(
							originalParams,
							launcherGroupComponentType,
							launchGroupId
						);
						return { appID: tab.componentType, individualParams };
					});
					// spawnTabCollection does NOT return a promise so instead we create a promise and resolve it in the callback
					return new Promise((resolve, reject) => {
						self.spawnTabCollection({ componentList, globalParams: component.spawnOptions }, (err, spawnResponses) => {
							if (!!err) {
								reject(err);
							} else {
								// err is logged in SpawnTabCollection. It's possible to end up with some windows that fail to spawn and some that succeed (the collection will be formed with the windows that spawn)
								spawnResponses?.windows.forEach((item) => responses.push(item));
								resolve(responses);
							}
						});
					});
				} else {
					//config error...
					Logger.system.error(
						`Component specified had neither a componentType or components value, unable to spawn.\nProblem element:\n${JSON.stringify(
							component,
							null,
							2
						)}`
					);
					return null;
				}
			})
		).then(() => responses);
	}

	_compileSpawnParams(component: ComponentConfig) {
		const { spawnOptions, launchGroupComponentData, componentType } = component;
		const spawnParams: SpawnParams = { ...spawnOptions };
		spawnParams.component = componentType;
		spawnParams.launchGroupComponentData = launchGroupComponentData;
		return spawnParams;
	}

	/**
	 * Splintering.
	 */
	update() {
		RouterClient.publish("Launcher.update", {
			componentList: Components,
		});

		ConfigClient.set(["finsemble", "components"], this.cleanComponents(Components));
	}

	cleanComponents(components: any): any {
		const componentsCopy = structuredClone(components);
		Object.values(componentsCopy).forEach((component: any) => {
			delete component.icons;
			delete component.component.type;
		});
		return componentsCopy;
	}

	async prepareCloneParams(
		toClone: WindowIdentifier,
		spawnParams: SpawnParams
	): Promise<{ error?: StandardError; spawnParams?: SpawnParams }> {
		const paramsForClone: SpawnParams = {
			options: {},
		};

		const windowName =
			toClone?.windowName || spawnParams.relativeWindow?.windowName || spawnParams.launchingWindow?.windowName;

		if (!windowName) {
			return { error: "No windowName specified in clone" };
		}

		const { wrap } = await FinsembleWindow.getInstance({
			windowName,
			waitForReady: true,
		});

		const { err, data: windowOptions } = await wrap.getOptions();
		if (err) {
			return { error: err };
		}

		if (windowOptions) {
			if (windowOptions.componentType === "Toolbar") {
				return { error: `Cannot clone Toolbar ${windowName}` };
			}
			if (windowOptions.componentType === "Freestanding") {
				return { error: `Cannot clone Freestanding app ${windowName}` };
			}

			if (windowOptions.customData?.component?.singleton) {
				return { error: `Cannot clone singleton app ${windowName}` };
			}
			paramsForClone.component = windowOptions.componentType;
			// Not 100% sure what should and should not be on the WindowOptions type.
			// I get the impression the type and it what it's used for are not aligned
			paramsForClone.left = windowOptions.left;
			paramsForClone.top = windowOptions.top;
			paramsForClone.width = windowOptions.width;
			paramsForClone.height = windowOptions.height;

			paramsForClone.maxHeight = windowOptions.maxHeight;
			paramsForClone.maxWidth = windowOptions.maxWidth;

			if (windowOptions.windowType == "WebWindow") {
				paramsForClone.url = windowOptions.url;
			}
			paramsForClone.path = windowOptions.path;
			paramsForClone.arguments = windowOptions.arguments;

			paramsForClone.windowTitleBarUrl = windowOptions.windowTitleBarUrl;
			paramsForClone.options = {
				alwaysOnTop: windowOptions.alwaysOnTop,
				cornerRounding: windowOptions.cornerRounding,
				opacity: windowOptions.opacity,
				autoShow: windowOptions.show,
				resizable: windowOptions.resizable,
				showTaskbarIcon: windowOptions.showTaskbarIcon,
				transparent: windowOptions.transparent,
			};
		}

		paramsForClone.position = "absolute";

		const staggerPixels = this.getStaggerPixels(spawnParams as AdjustedSpawnParams);
		paramsForClone.staggerPixels = staggerPixels;
		paramsForClone.top = Number(paramsForClone.top) + staggerPixels;
		paramsForClone.left = Number(paramsForClone.left) + staggerPixels;

		const { data: componentState } = await wrap.getComponentState({});

		if (componentState) {
			paramsForClone.addToWorkspace = true;
			paramsForClone.componentState = componentState;
			const { persistedTitle } = componentState as any;
			if (persistedTitle) {
				paramsForClone.tabTitle = persistedTitle;
				/**
				 * componentState gets stored later in the spawn process. Delete persistedTitle because tabTitle's
				 * mechanism already sets the component state and this value can be overridden by the user.
				 *
				 * Delete so there are no conflicting values.
				 */
				delete (componentState as any).persistedTitle;
			}
		}

		return {
			spawnParams: jsonMergePatch.apply(paramsForClone, spawnParams),
		};
	}
}
Logger.start();
